import {
  movingCost,
  normalizeSerial,
  requestExpired,
  type CostMovement,
  type StockLevel,
  type SyncOp,
  TICKET_CLEARS_MS,
} from '@ims/core'
import {
  type AssignedCustom,
  type CustomSales,
  decodeRow,
  isTableName,
  tablesByName,
  type CommitResult,
  type CounterRow,
  type InvoiceDetail,
  type InvoiceFilter,
  type GoodsReceiptDetail,
  type GoodsReceiptFilter,
  type GoodsReceiptSummary,
  type PayablesRepository,
  type PartiesRepository,
  type CustomerLedger,
  type ProductSummary,
  type PartyBoardFilter,
  type PartyStanding,
  type SupplierBillDetail,
  type SupplierBillFilter,
  type SupplierBillSummary,
  type SupplierCreditSummary,
  type SupplierOwed,
  type SupplierPaymentSummary,
  type NumberedDoc,
  type OpenPurchaseOrder,
  type OpenSalesOrder,
  type QuotationOutcome,
  type PurchaseOrderDetail,
  type PurchaseOrderBoardFilter,
  type SalesOrderBoardFilter,
  type SalesOrderBoardRow,
  type SalesOrderCounts,
  type PurchaseOrderBoardRow,
  type PurchaseOrderCounts,
  type ToOrderRow,
  type PurchaseRequestBoardFilter,
  type PurchaseRequestBoardRow,
  type PurchaseRequestCounts,
  type PurchaseRequestDetail,
  type PurchaseRequestRow,
  type RequestLineLeft,
  type RequestOrderRef,
  type PurchaseOrderFilter,
  type PurchaseOrderRow,
  type SalesOrderDetail,
  type SalesOrderFilter,
  type SalesOrderRow,
  type InvoiceRow,
  type ListOptions,
  type MovementRow,
  type LastSale,
  type GuestRequest,
  type KitchenQueue,
  type KitchenTicket,
  type KotTicketRow,
  type DeliveryDetail,
  type DeliveryRow,
  type OrderRow,
  type OpenInvoice,
  type OrderDetail,
  type OrderItemRow,
  type ProductCostBasis,
  type ProductProfitDay,
  type ProductProfitSales,
  type ProductSales,
  type DaySale,
  type SalesDay,
  type VatDay,
  type Page,
  type BatchLevel,
  type CodeMatch,
  type ProductRow,
  type ProductScope,
  type Repository,
  type RowOf,
  type SqliteValue,
  type TableName,
  type TenantSettingsRow,
  type PaidOrder,
  type PaymentMethod,
  type KotItemRow,
  type ActivityRows,
  type InvoiceOrderInfo,
  type ServiceGroupSales,
} from '@ims/data'
import { placeholders } from './db.ts'
import type { SqlDriver, SqlParam, SqlRow } from './driver.ts'
import type { SyncEngine } from './sync-engine.ts'

const DEFAULT_LIMIT = 50

// Supplier bills (docs/plans/supplier-bills.md). What a bill `b` is covered by: the
// allocations of payments and credits that are still active. Owed is never stored.
// Same balance rule as invoices.detail: total − active payments − finalized credit notes.
// The finalized bills that still owe something, one row each.
const OPEN_INVOICES = `SELECT * FROM (
   SELECT i.id AS invoiceId, i.number, i.customer_id AS customerId,
          i.customer_name AS customerName, i.invoice_date AS invoiceDate,
          i.due_date AS dueDate,
          i.total_paisa - COALESCE(p.paid, 0) - COALESCE(c.credited, 0) AS balance
     FROM sales_invoices i
     LEFT JOIN (SELECT a.invoice_id, SUM(a.amount_paisa) AS paid
                  FROM payment_allocations a JOIN payments p ON p.id = a.payment_id
                 WHERE a.deleted_at IS NULL AND p.status = 'active' AND p.deleted_at IS NULL
                 GROUP BY a.invoice_id) p ON p.invoice_id = i.id
     LEFT JOIN (SELECT invoice_id, SUM(total_paisa) AS credited
                  FROM credit_notes WHERE status = 'finalized' AND deleted_at IS NULL
                 GROUP BY invoice_id) c ON c.invoice_id = i.id
    WHERE i.deleted_at IS NULL AND i.status = 'finalized')
 WHERE balance > 0`

const LIVE = `b.deleted_at IS NULL AND b.status = 'active'`
const COVERED = `(SELECT COALESCE(SUM(a.amount_paisa), 0) FROM supplier_allocations a
   LEFT JOIN supplier_payments p ON p.id = a.payment_id
   LEFT JOIN supplier_credits c ON c.id = a.credit_id
  WHERE a.bill_id = b.id AND a.deleted_at IS NULL AND COALESCE(p.status, c.status) = 'active')`
/** The live bill of the receipt `g`, to be closed with further conditions. */
const LIVE_BILL = `SELECT 1 FROM supplier_bills b WHERE b.goods_receipt_id = g.id AND ${LIVE}`

// A product's stock across all locations, for a query over `products`.
const STOCK_OF = `(SELECT COALESCE(SUM(s.qty_milli), 0) FROM current_stock s WHERE s.product_id = products.id)`

// What each customer or supplier owes, one row per one who owes something (`?` = today):
// the amount, how many open bills, how many past their due date, the oldest open bill's
// date and the earliest due date. Same rules as `openInvoices` and `payables.owed`.
const PARTY_OPEN = {
  customers: `SELECT customerId AS pid, SUM(balance) AS owed, COUNT(*) AS bills,
                     SUM(CASE WHEN dueDate IS NOT NULL AND substr(dueDate, 1, 10) < ? THEN 1 ELSE 0 END) AS late,
                     MIN(invoiceDate) AS oldest, MIN(dueDate) AS first_due
                FROM (${OPEN_INVOICES}) WHERE customerId IS NOT NULL GROUP BY customerId`,
  suppliers: `SELECT b.supplier_id AS pid, SUM(b.total_paisa - ${COVERED}) AS owed, COUNT(*) AS bills,
                     SUM(CASE WHEN b.due_date IS NOT NULL AND b.due_date < ? THEN 1 ELSE 0 END) AS late,
                     MIN(b.bill_date) AS oldest, MIN(b.due_date) AS first_due
                FROM supplier_bills b WHERE ${LIVE} AND b.total_paisa > ${COVERED}
               GROUP BY b.supplier_id`,
}

// Each one's newest bill: a finalized invoice, or an active supplier bill (`rn = 1`).
const PARTY_LAST = {
  customers: `SELECT customer_id AS pid, invoice_date AS d, total_paisa AS t,
                     ROW_NUMBER() OVER (PARTITION BY customer_id ORDER BY invoice_date DESC, created_at DESC) AS rn
                FROM sales_invoices
               WHERE deleted_at IS NULL AND status = 'finalized' AND customer_id IS NOT NULL`,
  suppliers: `SELECT supplier_id AS pid, bill_date AS d, total_paisa AS t,
                     ROW_NUMBER() OVER (PARTITION BY supplier_id ORDER BY bill_date DESC, created_at DESC) AS rn
                FROM supplier_bills WHERE deleted_at IS NULL AND status = 'active'`,
}

function escapeLike(term: string): string {
  return term.replace(/[\\%_]/g, (c) => `\\${c}`)
}

/** Repository over the local SQLite cache. Every read works offline. */
/** SQL: line `l` has no active product assignment. */
const UNASSIGNED = `NOT EXISTS (SELECT 1 FROM invoice_line_products a
                 WHERE a.invoice_line_id = l.id AND a.deleted_at IS NULL)`

// Every quotation and sales order with what has been billed from it, one row each.
const SO_BOARD = `SELECT o.*, COUNT(l.id) AS _lines,
         COALESCE(SUM(l.qty_milli), 0) AS _ordered,
         COALESCE(SUM(MIN(l.qty_milli, COALESCE(b.qty, 0))), 0) AS _billed,
         COALESCE(SUM(MAX(0, l.qty_milli - COALESCE(b.qty, 0))), 0) AS _left,
         COALESCE(SUM(CAST(ROUND(l.total_paisa * MAX(0, l.qty_milli - COALESCE(b.qty, 0)) * 1.0
                                 / l.qty_milli) AS INTEGER)), 0) AS _unbilled,
         EXISTS (SELECT 1 FROM sales_orders x
                  WHERE x.source_id = o.id AND x.kind = 'order' AND o.kind = 'quotation'
                    AND x.status <> 'cancelled' AND x.deleted_at IS NULL) AS _made_order
    FROM sales_orders o
    LEFT JOIN sales_order_lines l ON l.sales_order_id = o.id AND l.deleted_at IS NULL
    LEFT JOIN (SELECT x.sales_order_line_id AS id, SUM(x.qty_milli) AS qty
                 FROM sales_invoice_lines x JOIN sales_invoices i ON i.id = x.invoice_id
                WHERE x.deleted_at IS NULL AND x.sales_order_line_id IS NOT NULL
                  AND i.status = 'finalized' AND i.deleted_at IS NULL
                GROUP BY x.sales_order_line_id) b ON b.id = l.id
   WHERE o.deleted_at IS NULL
   GROUP BY o.id`
// Sent, accepted or confirmed, with something still to bill here.
const SO_LIVE = `(status IN ('sent', 'accepted', 'confirmed') AND (_left > 0 OR _lines = 0) AND NOT _made_order)`
const SO_OPEN = `(status = 'draft' OR ${SO_LIVE})`
// A sent quotation past its date; takes today as its one parameter.
const SO_EXPIRED = `(kind = 'quotation' AND status = 'sent' AND valid_until < ?)`

// Every purchase order with what has arrived of it, one row each (the list and its counts).
const PO_BOARD = `SELECT po.*, s.name AS _supplier, COUNT(l.id) AS _lines,
         COALESCE(SUM(l.qty_ordered_milli), 0) AS _ordered,
         COALESCE(SUM(MIN(l.qty_ordered_milli, COALESCE(r.qty, 0))), 0) AS _received,
         COALESCE(SUM(MAX(0, l.qty_ordered_milli - COALESCE(r.qty, 0))), 0) AS _left,
         COALESCE(SUM(CAST(ROUND(MAX(0, l.qty_ordered_milli - COALESCE(r.qty, 0))
                                 * l.unit_cost_paisa / 1000.0) AS INTEGER)), 0) AS _due
    FROM purchase_orders po
    LEFT JOIN purchase_order_lines l ON l.purchase_order_id = po.id AND l.deleted_at IS NULL
    LEFT JOIN suppliers s ON s.id = po.supplier_id
    LEFT JOIN (SELECT purchase_order_line_id AS id, SUM(qty_milli) AS qty
                 FROM goods_receipt_lines
                WHERE deleted_at IS NULL AND purchase_order_line_id IS NOT NULL
                GROUP BY purchase_order_line_id) r ON r.id = l.id
   WHERE po.deleted_at IS NULL
   GROUP BY po.id`
// Still being worked on: a draft, or a sent order with something to arrive.
// Open: drafts, orders waiting for approval or approved but not sent, and sent ones still to arrive.
// How much of each request line is on purchase orders that are not cancelled or rejected
// (drafts count: the goods are being ordered). Summed, never stored.
const REQ_ORDERED = `SELECT pol.purchase_request_line_id AS line_id, SUM(pol.qty_ordered_milli) AS qty
    FROM purchase_order_lines pol JOIN purchase_orders po ON po.id = pol.purchase_order_id
   WHERE pol.deleted_at IS NULL AND po.deleted_at IS NULL AND pol.purchase_request_line_id IS NOT NULL
     AND po.status NOT IN ('cancelled', 'rejected')
   GROUP BY pol.purchase_request_line_id`

const REQ_BOARD = `SELECT r.*, loc.name AS _location, COUNT(l.id) AS _lines,
         COALESCE(SUM(CASE WHEN COALESCE(o.qty, 0) >= l.qty_milli THEN 1 ELSE 0 END), 0) AS _done,
         COALESCE(SUM(CASE WHEN COALESCE(o.qty, 0) > 0 THEN 1 ELSE 0 END), 0) AS _started
    FROM purchase_requests r
    LEFT JOIN purchase_request_lines l ON l.purchase_request_id = r.id AND l.deleted_at IS NULL
    LEFT JOIN (${REQ_ORDERED}) o ON o.line_id = l.id
    LEFT JOIN locations loc ON loc.id = r.location_id
   WHERE r.deleted_at IS NULL
   GROUP BY r.id`

// Open: still to decide, or approved with something left to order.
const REQ_OPEN = `(status IN ('draft', 'submitted') OR (status = 'approved' AND _done < _lines))`

// Generated (issued) orders are on order like sent ones (docs/plans/po-ready.md).
const PO_OPEN = `(status IN ('draft', 'submitted', 'approved') OR (status IN ('issued', 'sent') AND (_left > 0 OR _lines = 0)))`

export class LocalRepository implements Repository {
  readonly mode = 'local' as const
  readonly tenantId: string
  readonly sync: SyncEngine
  private readonly driver: SqlDriver
  private readonly installationId: string

  constructor(options: {
    driver: SqlDriver
    engine: SyncEngine
    tenantId: string
    installationId: string
  }) {
    this.driver = options.driver
    this.sync = options.engine
    this.tenantId = options.tenantId
    this.installationId = options.installationId
  }

  commit(ops: SyncOp[]): Promise<CommitResult> {
    return this.sync.commit(ops)
  }

  subscribe(listener: (tables: TableName[]) => void): () => void {
    return this.sync.onTablesChanged(listener)
  }

  private async rows<N extends TableName>(
    table: N,
    sql: string,
    params: readonly SqlParam[] = [],
  ): Promise<RowOf<N>[]> {
    const rows = await this.driver.all<SqlRow>(sql, params)
    return rows.map((r) => decodeRow(table, r as Record<string, SqliteValue>) as RowOf<N>)
  }

  /** Kitchen tickets picked by `sql`, each with the item rows it covers and its table's name. */
  private async kitchenTickets(sql: string): Promise<KitchenTicket[]> {
    const tickets = await this.rows('kot_tickets', sql)
    const out: KitchenTicket[] = []
    for (const ticket of tickets) {
      const [rows, table] = await Promise.all([
        this.rows(
          'order_items',
          `SELECT i.* FROM order_items i JOIN kot_items l ON l.order_item_id = i.id
           WHERE l.kot_id = ? AND l.deleted_at IS NULL ORDER BY i.created_at`,
          [ticket.id],
        ),
        this.driver.all<{
          name: string | null
          service_type: OrderRow['service_type']
          order_no: number | null
          label: string | null
          server_id: string | null
          status: OrderRow['status']
          closed_at: string | null
          void_reason: string | null
          merged_into_id: string | null
          invoice_id: string | null
        }>(
          `SELECT t.name, o.service_type, o.order_no, o.label, o.server_id, o.status, o.closed_at,
                  o.void_reason, o.merged_into_id, o.invoice_id
           FROM orders o LEFT JOIN dining_tables t ON t.id = o.table_id WHERE o.id = ?`,
          [ticket.order_id],
        ),
      ])
      const o = table[0]
      // A merged order is void but not cancelled: what counts is the order it went into.
      let end: Pick<
        OrderRow,
        'status' | 'closed_at' | 'void_reason' | 'merged_into_id' | 'invoice_id'
      > | null = o ?? null
      while (end?.merged_into_id) end = (await this.get('orders', end.merged_into_id)) ?? end
      const invoice =
        end?.status === 'closed' && end.invoice_id
          ? await this.get('sales_invoices', end.invoice_id)
          : null
      out.push({
        ticket,
        rows,
        tableName: o?.name ?? null,
        order: o
          ? {
              service_type: o.service_type,
              order_no: o.order_no,
              label: o.label,
              server_id: o.server_id,
              status: end!.status,
              closed_at: end!.closed_at,
              void_reason: end!.void_reason,
              on_credit: !!invoice && (await this.invoiceDue(invoice)) > 0,
            }
          : null,
      })
    }
    return out
  }

  private async count(sql: string, params: readonly SqlParam[]): Promise<number> {
    const rows = await this.driver.all<{ n: number }>(`SELECT COUNT(*) AS n FROM (${sql})`, params)
    return rows[0]?.n ?? 0
  }

  async get<N extends TableName>(table: N, id: string): Promise<RowOf<N> | null> {
    const rows = await this.rows(table, `SELECT * FROM ${table} WHERE id = ?`, [id])
    return rows[0] ?? null
  }

  async list<N extends TableName>(table: N, options: ListOptions<N> = {}): Promise<Page<RowOf<N>>> {
    if (!isTableName(table)) throw new Error(`unknown table ${table}`)
    const columns = tablesByName[table].columns as Record<string, { kind: string }>
    const where: string[] = []
    const params: SqlParam[] = []
    if (!options.includeDeleted) where.push('deleted_at IS NULL')
    for (const [key, value] of Object.entries(options.where ?? {})) {
      if (!Object.hasOwn(columns, key)) throw new Error(`unknown column ${table}.${key}`)
      if (value === null) {
        where.push(`${key} IS NULL`)
      } else {
        where.push(`${key} = ?`)
        params.push(typeof value === 'boolean' ? (value ? 1 : 0) : (value as SqlParam))
      }
    }
    const base = `SELECT * FROM ${table}${where.length ? ` WHERE ${where.join(' AND ')}` : ''}`
    let order = ''
    if (options.orderBy) {
      if (!Object.hasOwn(columns, options.orderBy.column))
        throw new Error(`unknown column ${options.orderBy.column}`)
      order = ` ORDER BY ${options.orderBy.column} ${options.orderBy.direction === 'desc' ? 'DESC' : 'ASC'}`
    }
    const limit = options.limit ?? DEFAULT_LIMIT
    const offset = options.offset ?? 0
    const [rows, total] = await Promise.all([
      this.rows(table, `${base}${order} LIMIT ? OFFSET ?`, [...params, limit, offset]),
      this.count(base, params),
    ])
    return { rows, total }
  }

  async settings(): Promise<TenantSettingsRow | null> {
    return this.get('tenant_settings', this.tenantId)
  }

  readonly products = {
    findByCode: async (code: string): Promise<ProductRow | null> =>
      (await this.products.lookupCode(code))?.product ?? null,

    unitsUsed: async (productId: string): Promise<boolean> => {
      const rows = await this.driver.all<{ n: number }>(
        `SELECT (SELECT COUNT(*) FROM sales_invoice_lines
                  WHERE product_id = ? AND sold_unit IS NOT NULL AND deleted_at IS NULL)
              + (SELECT COUNT(*) FROM goods_receipt_lines
                  WHERE product_id = ? AND entered_unit IS NOT NULL AND deleted_at IS NULL) AS n`,
        [productId, productId],
      )
      return (rows[0]?.n ?? 0) > 0
    },
    lookupCode: async (code: string): Promise<CodeMatch | null> => {
      const trimmed = code.trim()
      if (!trimmed) return null
      const rows = await this.rows(
        'products',
        `SELECT * FROM products
          WHERE deleted_at IS NULL AND (barcode = ? OR lower(sku) = lower(?))
          ORDER BY (barcode = ?) DESC LIMIT 1`,
        [trimmed, trimmed, trimmed],
      )
      if (rows[0]) return { product: rows[0], pack: null }
      const packs = await this.rows(
        'product_codes',
        `SELECT pc.* FROM product_codes pc JOIN products p ON p.id = pc.product_id
          WHERE pc.deleted_at IS NULL AND p.deleted_at IS NULL AND pc.code = ? LIMIT 1`,
        [trimmed],
      )
      const pack = packs[0]
      if (pack) {
        const product = await this.get('products', pack.product_id)
        return product ? { product, pack } : null
      }
      // A serial number (IMEI) of one unit; units in stock first.
      const serials = await this.rows(
        'product_serials',
        `SELECT s.* FROM product_serials s JOIN products p ON p.id = s.product_id
          WHERE s.deleted_at IS NULL AND p.deleted_at IS NULL AND s.serial = ?
          ORDER BY (s.status = 'in_stock') DESC LIMIT 1`,
        [normalizeSerial(trimmed)],
      )
      const serial = serials[0]
      if (!serial) return null
      const product = await this.get('products', serial.product_id)
      return product ? { product, pack: null, serial } : null
    },

    search: async (
      term: string,
      options: {
        limit?: number
        offset?: number
        activeOnly?: boolean
        scope?: ProductScope
        forSale?: boolean
        categoryId?: string
        stock?: 'low' | 'out' | 'inactive' | 'service' | 'uncounted'
      } = {},
    ): Promise<Page<ProductRow>> => {
      const like = `%${escapeLike(term.trim())}%`
      const where = [
        `deleted_at IS NULL`,
        `(name LIKE ? ESCAPE '\\' OR sku LIKE ? ESCAPE '\\' OR barcode LIKE ? ESCAPE '\\' OR tags LIKE ? ESCAPE '\\'
          OR brand_id IN (SELECT id FROM brands WHERE deleted_at IS NULL AND name LIKE ? ESCAPE '\\')
          OR category_id IN (SELECT id FROM categories WHERE deleted_at IS NULL AND name LIKE ? ESCAPE '\\')
          OR id IN (SELECT product_id FROM product_codes WHERE deleted_at IS NULL AND code LIKE ? ESCAPE '\\'))`,
      ]
      if (options.activeOnly) where.push('is_active = 1')
      if (options.scope === 'sellable') where.push(`kind <> 'group'`)
      if (options.scope === 'grouped') where.push(`kind <> 'variant'`)
      if (options.forSale !== undefined) where.push(`for_sale = ${options.forSale ? 1 : 0}`)
      const params: SqlParam[] = [like, like, like, like, like, like, like]
      if (options.categoryId === 'none') where.push('category_id IS NULL')
      else if (options.categoryId) {
        where.push('category_id = ?')
        params.push(options.categoryId)
      }
      // Low and out follow core `stockStatus`; a group holds no stock, so its variants are listed.
      const byStock = options.stock === 'low' || options.stock === 'out'
      if (options.stock === 'inactive') where.push('is_active = 0')
      // Services and products sold without counting (docs/plans/services.md).
      if (options.stock === 'service') where.push('is_service = 1')
      if (options.stock === 'uncounted')
        where.push(`kind <> 'group' AND track_stock = 0 AND is_service = 0`)
      if (options.stock === 'low')
        where.push(
          `kind <> 'group' AND track_stock = 1 AND ${STOCK_OF} > 0 AND ${STOCK_OF} <= min_stock_milli`,
        )
      if (options.stock === 'out')
        where.push(`kind <> 'group' AND track_stock = 1 AND ${STOCK_OF} <= 0`)
      if (options.scope === 'catalog' && !term.trim() && !byStock) where.push('parent_id IS NULL')
      const base = `SELECT * FROM products WHERE ${where.join(' AND ')}`
      const [rows, total] = await Promise.all([
        this.rows('products', `${base} ORDER BY name LIMIT ? OFFSET ?`, [
          ...params,
          options.limit ?? DEFAULT_LIMIT,
          options.offset ?? 0,
        ]),
        this.count(base, params),
      ])
      return { rows, total }
    },

    summary: async (options: { forSale?: boolean } = {}): Promise<ProductSummary> => {
      const sold = options.forSale === undefined ? '' : ` AND for_sale = ${options.forSale ? 1 : 0}`
      const [counts, categories] = await Promise.all([
        this.driver.all<{
          total: number
          low: number
          out: number
          below: number
          inactive: number
        }>(
          `SELECT
             SUM(CASE WHEN parent_id IS NULL THEN 1 ELSE 0 END) AS total,
             SUM(CASE WHEN kind <> 'group' AND track_stock = 1 AND qty > 0 AND qty <= min_stock_milli THEN 1 ELSE 0 END) AS low,
             SUM(CASE WHEN kind <> 'group' AND track_stock = 1 AND qty <= 0 THEN 1 ELSE 0 END) AS out,
             SUM(CASE WHEN kind <> 'group' AND track_stock = 1 AND qty < 0 THEN 1 ELSE 0 END) AS below,
             SUM(CASE WHEN parent_id IS NULL AND is_active = 0 THEN 1 ELSE 0 END) AS inactive
             FROM (SELECT *, ${STOCK_OF} AS qty FROM products WHERE deleted_at IS NULL${sold})`,
        ),
        // Variants share their group's category, so a group counts once and its variants'
        // stock counts under the same category. Value: rounded per product, like mulDivRound.
        this.driver.all<{
          categoryId: string | null
          count: number
          low: number
          out: number
          valuePaisa: number
        }>(
          `SELECT category_id AS categoryId,
             SUM(CASE WHEN parent_id IS NULL THEN 1 ELSE 0 END) AS count,
             SUM(CASE WHEN kind <> 'group' AND track_stock = 1 AND qty > 0 AND qty <= min_stock_milli THEN 1 ELSE 0 END) AS low,
             SUM(CASE WHEN kind <> 'group' AND track_stock = 1 AND qty <= 0 THEN 1 ELSE 0 END) AS out,
             SUM(CASE WHEN kind <> 'group' AND qty > 0 AND cost_price_paisa IS NOT NULL
                 THEN (qty * cost_price_paisa + 500) / 1000 ELSE 0 END) AS valuePaisa
             FROM (SELECT *, ${STOCK_OF} AS qty FROM products WHERE deleted_at IS NULL${sold})
            GROUP BY category_id`,
        ),
      ])
      const c = counts[0]
      return {
        total: c?.total ?? 0,
        low: c?.low ?? 0,
        out: c?.out ?? 0,
        belowZero: c?.below ?? 0,
        inactive: c?.inactive ?? 0,
        categories,
      }
    },
  }

  readonly stock = {
    levels: async (
      filter: { productId?: string; locationId?: string } = {},
    ): Promise<StockLevel[]> => {
      const where: string[] = []
      const params: SqlParam[] = []
      if (filter.productId) {
        where.push('product_id = ?')
        params.push(filter.productId)
      }
      if (filter.locationId) {
        where.push('location_id = ?')
        params.push(filter.locationId)
      }
      return this.driver.all<StockLevel>(
        `SELECT product_id, location_id, qty_milli FROM current_stock${where.length ? ` WHERE ${where.join(' AND ')}` : ''}`,
        params,
      )
    },

    onOrder: async (): Promise<Record<string, number>> => {
      const rows = await this.driver.all<{ product_id: string; qty: number }>(
        `SELECT product_id, SUM(due) AS qty FROM (
           SELECT l.product_id,
                  MAX(0, l.qty_ordered_milli - COALESCE((
                    SELECT SUM(r.qty_milli) FROM goods_receipt_lines r
                     WHERE r.purchase_order_line_id = l.id AND r.deleted_at IS NULL), 0)) AS due
             FROM purchase_order_lines l JOIN purchase_orders o ON o.id = l.purchase_order_id
            WHERE o.status IN ('issued', 'sent') AND o.deleted_at IS NULL AND l.deleted_at IS NULL)
          GROUP BY product_id HAVING SUM(due) > 0`,
      )
      return Object.fromEntries(rows.map((r) => [r.product_id, r.qty]))
    },

    committed: async (
      filter: { locationId?: string; exceptOrderId?: string } = {},
    ): Promise<Record<string, number>> => {
      const where: string[] = []
      const params: SqlParam[] = []
      if (filter.locationId) {
        where.push('(o.location_id = ? OR o.location_id IS NULL)')
        params.push(filter.locationId)
      }
      if (filter.exceptOrderId) {
        where.push('o.id <> ?')
        params.push(filter.exceptOrderId)
      }
      const rows = await this.driver.all<{ product_id: string; qty: number }>(
        `SELECT product_id, SUM(due) AS qty FROM (
           SELECT l.product_id,
                  MAX(0, l.qty_milli - COALESCE((
                    SELECT SUM(b.qty_milli) FROM sales_invoice_lines b
                      JOIN sales_invoices i ON i.id = b.invoice_id
                     WHERE b.sales_order_line_id = l.id AND b.deleted_at IS NULL
                       AND i.status = 'finalized' AND i.deleted_at IS NULL), 0)) AS due
             FROM sales_order_lines l
             JOIN sales_orders o ON o.id = l.sales_order_id
             JOIN products p ON p.id = l.product_id
            WHERE o.kind = 'order' AND o.status = 'confirmed' AND o.deleted_at IS NULL
              AND l.deleted_at IS NULL AND p.track_stock = 1
              ${where.map((w) => `AND ${w}`).join(' ')})
          GROUP BY product_id HAVING SUM(due) > 0`,
        params,
      )
      return Object.fromEntries(rows.map((r) => [r.product_id, r.qty]))
    },

    batchLevels: async (
      filter: { productId?: string; productIds?: string[]; locationId?: string } = {},
    ): Promise<BatchLevel[]> => {
      const where: string[] = []
      const params: SqlParam[] = []
      if (filter.productId) {
        where.push('product_id = ?')
        params.push(filter.productId)
      }
      if (filter.productIds) {
        if (filter.productIds.length === 0) return []
        where.push(`product_id IN (${placeholders(filter.productIds)})`)
        params.push(...filter.productIds)
      }
      if (filter.locationId) {
        where.push('location_id = ?')
        params.push(filter.locationId)
      }
      return this.driver.all<BatchLevel>(
        `SELECT product_id, location_id, batch_id, qty_milli FROM batch_stock${where.length ? ` WHERE ${where.join(' AND ')}` : ''}`,
        params,
      )
    },

    movements: async (
      productId: string,
      options: { limit?: number; offset?: number; types?: MovementRow['type'][] } = {},
    ): Promise<Page<MovementRow>> => {
      const types = options.types ?? []
      const base = `SELECT * FROM stock_movements WHERE deleted_at IS NULL AND product_id = ?${
        types.length ? ` AND type IN (${types.map(() => '?').join(', ')})` : ''
      }`
      const params = [productId, ...types]
      const [rows, total] = await Promise.all([
        this.rows('stock_movements', `${base} ORDER BY occurred_at DESC LIMIT ? OFFSET ?`, [
          ...params,
          options.limit ?? DEFAULT_LIMIT,
          options.offset ?? 0,
        ]),
        this.count(base, params),
      ])
      return { rows, total }
    },
  }

  readonly orders = {
    open: async (): Promise<OrderDetail[]> => {
      const [orders, items, tickets, links] = await Promise.all([
        this.rows(
          'orders',
          `SELECT * FROM orders WHERE status IN ('open', 'billed') AND deleted_at IS NULL
           ORDER BY opened_at`,
        ),
        this.rows(
          'order_items',
          `SELECT i.* FROM order_items i JOIN orders o ON o.id = i.order_id
           WHERE o.status IN ('open', 'billed') AND o.deleted_at IS NULL AND i.deleted_at IS NULL
           ORDER BY i.created_at`,
        ),
        this.rows(
          'kot_tickets',
          `SELECT k.* FROM kot_tickets k JOIN orders o ON o.id = k.order_id
           WHERE o.status IN ('open', 'billed') AND o.deleted_at IS NULL AND k.deleted_at IS NULL
           ORDER BY k.sent_at`,
        ),
        this.rows(
          'kot_items',
          `SELECT l.* FROM kot_items l JOIN kot_tickets k ON k.id = l.kot_id
           JOIN orders o ON o.id = k.order_id
           WHERE o.status IN ('open', 'billed') AND o.deleted_at IS NULL AND k.deleted_at IS NULL
             AND l.deleted_at IS NULL`,
        ),
      ])
      const byOrder = new Map<string, OrderItemRow[]>()
      for (const item of items) {
        const list = byOrder.get(item.order_id)
        if (list) list.push(item)
        else byOrder.set(item.order_id, [item])
      }
      const ticketsOf = new Map<string, KotTicketRow[]>()
      for (const ticket of tickets) {
        const list = ticketsOf.get(ticket.order_id)
        if (list) list.push(ticket)
        else ticketsOf.set(ticket.order_id, [ticket])
      }
      // Which item rows each ticket covers: the Orders tab shows what is not sent yet,
      // and splits and merges orders from its list.
      const orderOfTicket = new Map(tickets.map((t) => [t.id, t.order_id]))
      const sentOf = new Map<string, KotItemRow[]>()
      for (const link of links) {
        const orderId = orderOfTicket.get(link.kot_id)
        if (!orderId) continue
        const list = sentOf.get(orderId)
        if (list) list.push(link)
        else sentOf.set(orderId, [link])
      }
      return orders.map((order) => ({
        order,
        items: byOrder.get(order.id) ?? [],
        tickets: ticketsOf.get(order.id) ?? [],
        sent: sentOf.get(order.id) ?? [],
      }))
    },

    detail: async (id: string): Promise<OrderDetail | null> => {
      const order = await this.get('orders', id)
      if (!order) return null
      const [items, tickets, sent] = await Promise.all([
        this.rows(
          'order_items',
          `SELECT * FROM order_items WHERE order_id = ? AND deleted_at IS NULL ORDER BY created_at`,
          [id],
        ),
        this.rows(
          'kot_tickets',
          `SELECT * FROM kot_tickets WHERE order_id = ? AND deleted_at IS NULL ORDER BY sent_at`,
          [id],
        ),
        this.rows(
          'kot_items',
          `SELECT l.* FROM kot_items l JOIN kot_tickets t ON t.id = l.kot_id
           WHERE t.order_id = ? AND l.deleted_at IS NULL AND t.deleted_at IS NULL`,
          [id],
        ),
      ])
      return { order, items, tickets, sent }
    },

    kitchen: () =>
      this.kitchenTickets(
        `SELECT * FROM kot_tickets WHERE status <> 'ready' AND deleted_at IS NULL ORDER BY sent_at`,
      ),

    ready: () =>
      this.kitchenTickets(
        `SELECT k.* FROM kot_tickets k JOIN orders o ON o.id = k.order_id
         WHERE k.status = 'ready' AND k.deleted_at IS NULL
           AND (o.status IN ('open', 'billed') OR (o.status = 'closed' AND k.ready_at > o.closed_at))
         ORDER BY k.ready_at DESC`,
      ),

    queue: async (): Promise<KitchenQueue> => {
      // The tickets the Kitchen screen has food to make for, only counted (`ticketStanding`);
      // and whether anything goes to the bar.
      const since = new Date(Date.now() - TICKET_CLEARS_MS).toISOString()
      const rows = await this.driver.all<{ kitchen: number; bar: number; has_bar: number }>(
        `WITH waiting AS (
           SELECT k.station FROM kot_tickets k
           JOIN orders o ON o.id = k.order_id
           LEFT JOIN orders m ON m.id = o.merged_into_id
           WHERE k.status <> 'ready' AND k.deleted_at IS NULL
             AND (COALESCE(m.status, o.status) IN ('open', 'billed')
                  OR (COALESCE(m.status, o.status) = 'closed'
                      AND COALESCE(m.closed_at, o.closed_at) > ?))
         )
         SELECT
           (SELECT COUNT(*) FROM waiting WHERE station = 'kitchen') AS kitchen,
           (SELECT COUNT(*) FROM waiting WHERE station = 'bar') AS bar,
           EXISTS (SELECT 1 FROM products
                    WHERE kitchen_station = 'bar' AND deleted_at IS NULL) AS has_bar`,
        [since],
      )
      const r = rows[0]
      return { kitchen: r?.kitchen ?? 0, bar: r?.bar ?? 0, hasBar: Boolean(r?.has_bar) }
    },

    requests: async (): Promise<GuestRequest[]> => {
      // A request whose order was paid or cancelled meanwhile is nobody's to decide;
      // one sent to a free table (no order yet) waits for a while, then drops out.
      const requests = await this.rows(
        'order_requests',
        `SELECT r.* FROM order_requests r LEFT JOIN orders o ON o.id = r.order_id
         WHERE r.status = 'pending' AND r.deleted_at IS NULL
           AND (r.order_id IS NULL OR o.status IN ('open', 'billed'))
         ORDER BY r.created_at`,
      )
      const out: GuestRequest[] = []
      const nowMs = Date.now()
      for (const request of requests) {
        if (requestExpired(request, nowMs)) continue
        const [items, table] = await Promise.all([
          this.rows(
            'order_request_items',
            `SELECT * FROM order_request_items WHERE request_id = ? AND deleted_at IS NULL
             ORDER BY created_at, description`,
            [request.id],
          ),
          this.driver.all<{ name: string }>(`SELECT name FROM dining_tables WHERE id = ?`, [
            request.table_id,
          ]),
        ])
        // The order's server hears it first; a free table's request has no order yet.
        const server = request.order_id
          ? await this.driver.all<{ server_id: string | null }>(
              `SELECT server_id FROM orders WHERE id = ?`,
              [request.order_id],
            )
          : []
        out.push({
          request,
          items,
          tableName: table[0]?.name ?? null,
          serverId: server[0]?.server_id ?? null,
        })
      }
      return out
    },

    lastKotNo: async (sinceIso: string): Promise<number> => {
      const rows = await this.driver.all<{ n: number | null }>(
        `SELECT MAX(kot_no) AS n FROM kot_tickets WHERE sent_at >= ? AND deleted_at IS NULL`,
        [sinceIso],
      )
      return rows[0]?.n ?? 0
    },

    lastOrderNo: async (sinceIso: string): Promise<number> => {
      const rows = await this.driver.all<{ n: number | null }>(
        `SELECT MAX(order_no) AS n FROM orders WHERE opened_at >= ? AND deleted_at IS NULL`,
        [sinceIso],
      )
      return rows[0]?.n ?? 0
    },

    paidOn: async (date: string): Promise<PaidOrder[]> => {
      const orders = await this.rows(
        'orders',
        `SELECT o.* FROM orders o JOIN sales_invoices i ON i.id = o.invoice_id
         WHERE o.status = 'closed' AND o.deleted_at IS NULL AND i.deleted_at IS NULL
           AND i.status IN ('finalized', 'cancelled') AND i.invoice_date = ?
         ORDER BY i.finalized_at DESC, o.closed_at DESC`,
        [date],
      )
      const out: PaidOrder[] = []
      for (const order of orders) {
        const [invoice, table, paid] = await Promise.all([
          this.get('sales_invoices', order.invoice_id!),
          order.table_id ? this.get('dining_tables', order.table_id) : null,
          this.driver.all<{ method: PaymentMethod }>(
            `SELECT DISTINCT p.method FROM payment_allocations a JOIN payments p ON p.id = a.payment_id
             WHERE a.invoice_id = ? AND a.deleted_at IS NULL AND p.deleted_at IS NULL
               AND p.status = 'active'
             ORDER BY p.method`,
            [order.invoice_id],
          ),
        ])
        if (!invoice) continue
        out.push({
          order,
          tableName: table?.name ?? null,
          invoice: {
            id: invoice.id,
            number: invoice.number,
            status: invoice.status,
            total_paisa: invoice.total_paisa,
            finalized_at: invoice.finalized_at,
          },
          methods: paid.map((p) => p.method),
        })
      }
      return out
    },
  }

  /** What is still unpaid on a finalized invoice: total − active payments − finalized credit notes. */
  private async invoiceDue(invoice: InvoiceRow): Promise<number> {
    const rows = await this.driver.all<{ paid: number | null; credited: number | null }>(
      `SELECT
         (SELECT SUM(a.amount_paisa) FROM payment_allocations a
            JOIN payments p ON p.id = a.payment_id
           WHERE a.invoice_id = ? AND a.deleted_at IS NULL AND p.status = 'active' AND p.deleted_at IS NULL) AS paid,
         (SELECT SUM(total_paisa) FROM credit_notes WHERE invoice_id = ? AND status = 'finalized') AS credited`,
      [invoice.id, invoice.id],
    )
    return invoice.total_paisa - (rows[0]?.paid ?? 0) - (rows[0]?.credited ?? 0)
  }

  readonly deliveries = {
    active: async (): Promise<DeliveryDetail[]> => {
      const since = new Date(Date.now() - 30 * 86_400_000).toISOString()
      const deliveries = await this.rows(
        'deliveries',
        `SELECT d.* FROM deliveries d JOIN orders o ON o.id = d.order_id
         LEFT JOIN sales_invoices i ON i.id = o.invoice_id
         WHERE d.deleted_at IS NULL AND (
           (d.status = 'pending' AND o.status IN ('open', 'billed'))
           OR d.status = 'out'
           OR (d.status IN ('delivered', 'failed') AND i.status = 'finalized' AND d.updated_at >= ?))
         ORDER BY o.opened_at`,
        [since],
      )
      const out: DeliveryDetail[] = []
      for (const delivery of deliveries) {
        const order = await this.get('orders', delivery.order_id)
        if (!order) continue
        const [customer, invoice] = await Promise.all([
          order.customer_id ? this.get('customers', order.customer_id) : null,
          order.invoice_id ? this.get('sales_invoices', order.invoice_id) : null,
        ])
        const duePaisa = invoice?.status === 'finalized' ? await this.invoiceDue(invoice) : null
        // Delivered and paid: nothing left to do.
        if (delivery.status === 'delivered' && (duePaisa ?? 0) <= 0) continue
        out.push({
          delivery,
          order,
          customerName: customer?.name ?? null,
          invoice: invoice
            ? {
                id: invoice.id,
                number: invoice.number,
                status: invoice.status,
                total_paisa: invoice.total_paisa,
              }
            : null,
          duePaisa,
        })
      }
      return out
    },

    forOrder: async (orderId: string): Promise<DeliveryRow | null> => {
      const rows = await this.rows(
        'deliveries',
        `SELECT * FROM deliveries WHERE order_id = ? AND deleted_at IS NULL LIMIT 1`,
        [orderId],
      )
      return rows[0] ?? null
    },
  }

  readonly invoices = {
    detail: async (id: string): Promise<InvoiceDetail | null> => {
      const invoice = await this.get('sales_invoices', id)
      if (!invoice) return null
      const [lines, movements, assignments, balance] = await Promise.all([
        this.rows(
          'sales_invoice_lines',
          `SELECT * FROM sales_invoice_lines WHERE invoice_id = ? AND deleted_at IS NULL ORDER BY line_no`,
          [id],
        ),
        this.rows(
          'stock_movements',
          `SELECT * FROM stock_movements WHERE ref_id = ? AND deleted_at IS NULL ORDER BY occurred_at`,
          [id],
        ),
        this.rows(
          'invoice_line_products',
          `SELECT * FROM invoice_line_products WHERE invoice_id = ? AND deleted_at IS NULL ORDER BY created_at`,
          [id],
        ),
        this.driver.all<{ paid: number | null; credited: number | null }>(
          `SELECT
             (SELECT SUM(a.amount_paisa) FROM payment_allocations a
                JOIN payments p ON p.id = a.payment_id
               WHERE a.invoice_id = ? AND a.deleted_at IS NULL AND p.status = 'active' AND p.deleted_at IS NULL) AS paid,
             (SELECT SUM(total_paisa) FROM credit_notes WHERE invoice_id = ? AND status = 'finalized') AS credited`,
          [id, id],
        ),
      ])
      const b = balance[0]
      return {
        invoice,
        lines,
        movements,
        assignments,
        balancePaisa:
          invoice.status === 'finalized'
            ? invoice.total_paisa - (b?.paid ?? 0) - (b?.credited ?? 0)
            : null,
      }
    },

    list: async (filter: InvoiceFilter = {}): Promise<Page<InvoiceRow>> => {
      const where = ['deleted_at IS NULL']
      const params: SqlParam[] = []
      if (filter.status) {
        where.push('status = ?')
        params.push(filter.status)
      }
      if (filter.customerId) {
        where.push('customer_id = ?')
        params.push(filter.customerId)
      }
      if (filter.from) {
        where.push('invoice_date >= ?')
        params.push(filter.from)
      }
      if (filter.to) {
        where.push('invoice_date <= ?')
        params.push(filter.to)
      }
      if (filter.search?.trim()) {
        const like = `%${escapeLike(filter.search.trim())}%`
        // A restaurant bill is also found by its table's name.
        where.push(`(number LIKE ? ESCAPE '\\' OR customer_name LIKE ? ESCAPE '\\'
          OR id IN (SELECT o.invoice_id FROM orders o JOIN dining_tables t ON t.id = o.table_id
                     WHERE o.deleted_at IS NULL AND t.name LIKE ? ESCAPE '\\'))`)
        params.push(like, like, like)
      }
      if (filter.payment) {
        // Same balance rule as invoices.detail and reports.openInvoices.
        const balance = `(total_paisa
          - COALESCE((SELECT SUM(a.amount_paisa)
                        FROM payment_allocations a JOIN payments p ON p.id = a.payment_id
                       WHERE a.invoice_id = sales_invoices.id AND a.deleted_at IS NULL
                         AND p.status = 'active' AND p.deleted_at IS NULL), 0)
          - COALESCE((SELECT SUM(c.total_paisa) FROM credit_notes c
                       WHERE c.invoice_id = sales_invoices.id AND c.status = 'finalized'
                         AND c.deleted_at IS NULL), 0))`
        const late = `(due_date IS NOT NULL AND substr(due_date, 1, 10) < date('now', 'localtime'))`
        const rule = {
          paid: `${balance} <= 0`,
          due: `${balance} > 0`,
          overdue: `${balance} > 0 AND ${late}`,
          partial: `${balance} > 0 AND ${balance} < total_paisa AND NOT ${late}`,
          unpaid: `${balance} >= total_paisa AND ${balance} > 0 AND NOT ${late}`,
        }[filter.payment]
        where.push(`status = 'finalized' AND ${rule}`)
      }
      if (filter.unassignedCustom) {
        where.push(`status = 'finalized' AND EXISTS (
          SELECT 1 FROM sales_invoice_lines l
           WHERE l.invoice_id = sales_invoices.id AND l.deleted_at IS NULL AND l.product_id IS NULL
             AND ${UNASSIGNED})`)
      }
      const base = `SELECT * FROM sales_invoices WHERE ${where.join(' AND ')}`
      const [rows, total] = await Promise.all([
        this.rows(
          'sales_invoices',
          `${base} ORDER BY invoice_date DESC, created_at DESC LIMIT ? OFFSET ?`,
          [...params, filter.limit ?? DEFAULT_LIMIT, filter.offset ?? 0],
        ),
        this.count(base, params),
      ])
      return { rows, total }
    },

    orderInfo: async (ids: readonly string[]): Promise<InvoiceOrderInfo[]> => {
      if (ids.length === 0) return []
      return this.driver.all<InvoiceOrderInfo>(
        `SELECT o.invoice_id AS invoiceId, o.service_type AS serviceType, t.name AS tableName,
                o.covers AS covers, o.order_no AS orderNo, o.label AS label,
                COALESCE(o.server_id, o.created_by) AS serverId
           FROM orders o LEFT JOIN dining_tables t ON t.id = o.table_id
          WHERE o.deleted_at IS NULL AND o.invoice_id IN (${placeholders(ids)})`,
        [...ids],
      )
    },
  }

  /**
   * Finalized bills of a period grouped by `key` over their order (`o`): bills, guests and
   * items taken off by invoice date, returns by credit note date, as the Sales report counts.
   */
  private async groupSales(
    key: string,
    only: string,
    from: string,
    to: string,
  ): Promise<ServiceGroupSales[]> {
    const order = `LEFT JOIN orders o ON o.invoice_id = i.id AND o.deleted_at IS NULL`
    const [bills, returns] = await Promise.all([
      this.driver.all<Omit<ServiceGroupSales, 'returns'>>(
        `SELECT ${key} AS key, COUNT(*) AS bills, COALESCE(SUM(o.covers), 0) AS guests,
                SUM(i.total_paisa) AS sales, COALESCE(SUM(v.n), 0) AS voids,
                COALESCE(SUM(v.amount), 0) AS voidAmount
           FROM sales_invoices i ${order}
           LEFT JOIN (SELECT order_id, COUNT(*) AS n,
                             SUM(CAST(ROUND(-qty_milli * unit_price_paisa / 1000.0) AS INTEGER)) AS amount
                        FROM order_items WHERE qty_milli < 0 AND deleted_at IS NULL
                       GROUP BY order_id) v ON v.order_id = o.id
          WHERE i.deleted_at IS NULL AND i.status = 'finalized' AND i.invoice_date BETWEEN ? AND ?
                ${only}
          GROUP BY ${key}`,
        [from, to],
      ),
      this.driver.all<{ key: string | null; returns: number }>(
        `SELECT ${key} AS key, SUM(c.total_paisa) AS returns
           FROM credit_notes c JOIN sales_invoices i ON i.id = c.invoice_id ${order}
          WHERE c.deleted_at IS NULL AND c.status = 'finalized' AND c.note_date BETWEEN ? AND ?
                ${only}
          GROUP BY ${key}`,
        [from, to],
      ),
    ])
    const out = new Map<string | null, ServiceGroupSales>(
      bills.map((b) => [b.key, { ...b, returns: 0 }]),
    )
    for (const r of returns) {
      const row = out.get(r.key) ?? {
        key: r.key,
        bills: 0,
        guests: 0,
        sales: 0,
        returns: 0,
        voids: 0,
        voidAmount: 0,
      }
      out.set(r.key, { ...row, returns: r.returns })
    }
    return [...out.values()]
  }

  // Reports: sums over finalized documents only. Returns are finalized credit
  // notes, dated by note_date, so a return lands in the period it happened.
  readonly reports = {
    needsLookCount: async (from: string, to: string, missedBefore: string): Promise<number> => {
      const rows = await this.driver.all<{ n: number }>(
        `SELECT
           (SELECT COUNT(*) FROM order_items v
             WHERE v.deleted_at IS NULL AND v.void_of_id IS NOT NULL
               AND v.created_at >= ? AND v.created_at < ?
               AND EXISTS (SELECT 1 FROM kot_items k
                            WHERE k.order_item_id = v.void_of_id AND k.deleted_at IS NULL))
         + (SELECT COUNT(*) FROM orders o
             WHERE o.deleted_at IS NULL AND o.status = 'void' AND o.merged_into_id IS NULL
               AND o.closed_at >= ? AND o.closed_at < ?
               AND (SELECT COALESCE(SUM(i.qty_milli), 0) FROM order_items i
                     WHERE i.order_id = o.id AND i.deleted_at IS NULL) > 0)
         + (SELECT COUNT(*) FROM deliveries d
             WHERE d.deleted_at IS NULL AND d.status = 'failed'
               AND d.updated_at >= ? AND d.updated_at < ?)
         + (SELECT COUNT(*) FROM sales_invoices b
             WHERE b.deleted_at IS NULL AND b.status = 'cancelled'
               AND b.cancelled_at >= ? AND b.cancelled_at < ?)
         + (SELECT COUNT(*) FROM order_requests r
             WHERE r.deleted_at IS NULL AND r.status = 'pending' AND r.order_id IS NULL
               AND r.created_at >= ? AND r.created_at < ? AND r.created_at < ?) AS n`,
        [from, to, from, to, from, to, from, to, from, to, missedBefore],
      )
      return rows[0]?.n ?? 0
    },

    activity: async (from: string, to: string): Promise<ActivityRows> => {
      const span = [from, to]
      const [orders, voids, tickets, requests, deliveries, invoices, creditNotes] =
        await Promise.all([
          this.rows(
            'orders',
            `SELECT * FROM orders WHERE deleted_at IS NULL
               AND ((opened_at >= ? AND opened_at < ?) OR (closed_at >= ? AND closed_at < ?))`,
            [...span, ...span],
          ),
          this.rows(
            'order_items',
            `SELECT * FROM order_items WHERE deleted_at IS NULL AND void_of_id IS NOT NULL
               AND created_at >= ? AND created_at < ?`,
            span,
          ),
          this.rows(
            'kot_tickets',
            `SELECT * FROM kot_tickets WHERE deleted_at IS NULL AND ready_at >= ? AND ready_at < ?`,
            span,
          ),
          this.rows(
            'order_requests',
            `SELECT * FROM order_requests WHERE deleted_at IS NULL
               AND ((created_at >= ? AND created_at < ?) OR (decided_at >= ? AND decided_at < ?))`,
            [...span, ...span],
          ),
          this.rows(
            'deliveries',
            `SELECT * FROM deliveries WHERE deleted_at IS NULL AND status <> 'pending'
               AND ((dispatched_at >= ? AND dispatched_at < ?)
                 OR (delivered_at >= ? AND delivered_at < ?)
                 OR (status = 'failed' AND updated_at >= ? AND updated_at < ?))`,
            [...span, ...span, ...span],
          ),
          this.rows(
            'sales_invoices',
            `SELECT * FROM sales_invoices WHERE deleted_at IS NULL AND status <> 'draft'
               AND ((finalized_at >= ? AND finalized_at < ?)
                 OR (cancelled_at >= ? AND cancelled_at < ?))`,
            [...span, ...span],
          ),
          this.rows(
            'credit_notes',
            `SELECT * FROM credit_notes WHERE deleted_at IS NULL AND status = 'finalized'
               AND finalized_at >= ? AND finalized_at < ?`,
            span,
          ),
        ])
      const [payments, receipts, adjustments, tables, prints] = await Promise.all([
        this.driver.all<SqlRow>(
          `SELECT p.*, (SELECT a.invoice_id FROM payment_allocations a
                         WHERE a.payment_id = p.id AND a.deleted_at IS NULL LIMIT 1) AS invoice_id
             FROM payments p
            WHERE p.deleted_at IS NULL AND p.status = 'active'
              AND p.created_at >= ? AND p.created_at < ?`,
          span,
        ),
        this.driver.all<SqlRow>(
          `SELECT r.*, s.name AS supplier_name FROM goods_receipts r
             LEFT JOIN suppliers s ON s.id = r.supplier_id
            WHERE r.deleted_at IS NULL AND r.created_at >= ? AND r.created_at < ?`,
          span,
        ),
        this.driver.all<SqlRow>(
          `SELECT m.*, p.name AS product_name FROM stock_movements m
             LEFT JOIN products p ON p.id = m.product_id
            WHERE m.deleted_at IS NULL AND m.type IN ('adjustment', 'count')
              AND m.occurred_at >= ? AND m.occurred_at < ?`,
          span,
        ),
        this.driver.all<{ id: string; name: string }>(
          `SELECT id, name FROM dining_tables WHERE deleted_at IS NULL`,
        ),
        this.rows(
          'invoice_prints',
          `SELECT * FROM invoice_prints WHERE deleted_at IS NULL AND copy_no > 0
             AND printed_at >= ? AND printed_at < ?`,
          span,
        ),
      ])
      const idsOf = (values: (string | null | undefined)[]) => [
        ...new Set(values.filter((v): v is string => !!v)),
      ]
      const voided = idsOf(voids.map((v) => v.void_of_id))
      const sent =
        voided.length === 0
          ? []
          : await this.driver.all<{ id: string }>(
              `SELECT DISTINCT order_item_id AS id FROM kot_items
                WHERE deleted_at IS NULL AND order_item_id IN (${placeholders(voided)})`,
              voided,
            )
      const cancelled = orders
        .filter((o) => o.status === 'void' && !o.merged_into_id)
        .map((o) => o.id)
      const withItems =
        cancelled.length === 0
          ? []
          : await this.driver.all<{ id: string }>(
              // What was still on it: ordered rows less the rows that cancel them.
              `SELECT order_id AS id FROM order_items
                WHERE deleted_at IS NULL AND order_id IN (${placeholders(cancelled)})
                GROUP BY order_id HAVING SUM(qty_milli) > 0`,
              cancelled,
            )
      const have = new Set(orders.map((o) => o.id))
      const moreOrders = idsOf([
        ...voids.map((v) => v.order_id),
        ...tickets.map((t) => t.order_id),
        ...deliveries.map((d) => d.order_id),
        ...requests.map((r) => r.order_id),
        ...orders.map((o) => o.merged_into_id),
      ]).filter((id) => !have.has(id))
      const otherOrders =
        moreOrders.length === 0
          ? []
          : await this.rows(
              'orders',
              `SELECT * FROM orders WHERE id IN (${placeholders(moreOrders)})`,
              moreOrders,
            )
      const haveInvoices = new Set(invoices.map((i) => i.id))
      const moreInvoices = idsOf([
        ...orders.map((o) => o.invoice_id),
        ...creditNotes.map((n) => n.invoice_id),
        ...payments.map((p) => p.invoice_id as string | null),
        ...prints.map((p) => p.invoice_id),
      ]).filter((id) => !haveInvoices.has(id))
      const otherInvoices =
        moreInvoices.length === 0
          ? []
          : await this.rows(
              'sales_invoices',
              `SELECT * FROM sales_invoices WHERE id IN (${placeholders(moreInvoices)})`,
              moreInvoices,
            )
      return {
        orders,
        voids,
        sentItemIds: sent.map((s) => s.id),
        cancelledWithItems: withItems.map((w) => w.id),
        tickets,
        requests,
        deliveries,
        invoices,
        creditNotes,
        prints,
        payments: payments as unknown as ActivityRows['payments'],
        receipts: receipts as unknown as ActivityRows['receipts'],
        adjustments: adjustments as unknown as ActivityRows['adjustments'],
        otherOrders,
        otherInvoices,
        tables,
      }
    },

    openSalesOrders: (): Promise<OpenSalesOrder[]> =>
      this.driver.all<OpenSalesOrder>(
        `SELECT o.id AS orderId, o.number, o.doc_date AS docDate, o.expected_date AS expectedDate,
                o.customer_id AS customerId, o.customer_name AS customerName,
                o.total_paisa AS totalPaisa,
                SUM(l.qty_milli) AS orderedMilli,
                SUM(MIN(l.qty_milli, COALESCE(b.qty, 0))) AS billedMilli,
                SUM(CAST(ROUND(l.total_paisa * MAX(0, l.qty_milli - COALESCE(b.qty, 0)) * 1.0
                               / l.qty_milli) AS INTEGER)) AS unbilledPaisa
           FROM sales_orders o
           JOIN sales_order_lines l ON l.sales_order_id = o.id AND l.deleted_at IS NULL
           LEFT JOIN (SELECT x.sales_order_line_id AS id, SUM(x.qty_milli) AS qty
                        FROM sales_invoice_lines x JOIN sales_invoices i ON i.id = x.invoice_id
                       WHERE x.deleted_at IS NULL AND x.sales_order_line_id IS NOT NULL
                         AND i.status = 'finalized' AND i.deleted_at IS NULL
                       GROUP BY x.sales_order_line_id) b ON b.id = l.id
          WHERE o.kind = 'order' AND o.status = 'confirmed' AND o.deleted_at IS NULL
          GROUP BY o.id
         HAVING SUM(MAX(0, l.qty_milli - COALESCE(b.qty, 0))) > 0
          ORDER BY COALESCE(o.expected_date, o.doc_date), o.created_at`,
      ),

    openPurchaseOrders: (): Promise<OpenPurchaseOrder[]> =>
      this.driver.all<OpenPurchaseOrder>(
        `SELECT o.id AS orderId, o.number, o.order_date AS orderDate,
                o.expected_date AS expectedDate, o.supplier_id AS supplierId,
                s.name AS supplierName, o.total_paisa AS totalPaisa,
                SUM(l.qty_ordered_milli) AS orderedMilli,
                SUM(MIN(l.qty_ordered_milli, COALESCE(r.qty, 0))) AS receivedMilli,
                SUM(CAST(ROUND(MAX(0, l.qty_ordered_milli - COALESCE(r.qty, 0))
                               * l.unit_cost_paisa / 1000.0) AS INTEGER)) AS duePaisa
           FROM purchase_orders o
           JOIN purchase_order_lines l ON l.purchase_order_id = o.id AND l.deleted_at IS NULL
           LEFT JOIN suppliers s ON s.id = o.supplier_id
           LEFT JOIN (SELECT purchase_order_line_id AS id, SUM(qty_milli) AS qty
                        FROM goods_receipt_lines
                       WHERE deleted_at IS NULL AND purchase_order_line_id IS NOT NULL
                       GROUP BY purchase_order_line_id) r ON r.id = l.id
          WHERE o.status IN ('issued', 'sent') AND o.deleted_at IS NULL
          GROUP BY o.id
         HAVING SUM(MAX(0, l.qty_ordered_milli - COALESCE(r.qty, 0))) > 0
          ORDER BY COALESCE(o.expected_date, o.order_date), o.created_at`,
      ),

    quotationOutcomes: (from: string, to: string): Promise<QuotationOutcome[]> =>
      this.driver.all<QuotationOutcome>(
        `SELECT q.id, q.status, q.valid_until AS validUntil, q.total_paisa AS totalPaisa,
                (EXISTS (SELECT 1 FROM sales_invoices i
                          WHERE i.sales_order_id = q.id AND i.status = 'finalized'
                            AND i.deleted_at IS NULL)
                 OR EXISTS (SELECT 1 FROM sales_orders o
                             WHERE o.source_id = q.id AND o.kind = 'order'
                               AND o.status <> 'cancelled' AND o.deleted_at IS NULL)) AS won
           FROM sales_orders q
          WHERE q.kind = 'quotation' AND q.status <> 'draft' AND q.deleted_at IS NULL
            AND NOT (q.status = 'cancelled' AND q.close_reason = 'Revised')
            AND substr(q.doc_date, 1, 10) BETWEEN ? AND ?`,
        [from, to],
      ),

    salesByDay: (from: string, to: string): Promise<SalesDay[]> =>
      this.driver.all<SalesDay>(
        `SELECT date, SUM(invoices) AS invoices, SUM(total) AS total, SUM(taxable) AS taxable,
                SUM(tax) AS tax, SUM(discount) AS discount, SUM(returns) AS returns,
                SUM(returnsTaxable) AS returnsTaxable, SUM(returnsTax) AS returnsTax
           FROM (
             SELECT invoice_date AS date, COUNT(*) AS invoices, SUM(total_paisa) AS total,
                    SUM(taxable_paisa) AS taxable, SUM(tax_paisa) AS tax,
                    SUM(discount_paisa) AS discount, 0 AS returns, 0 AS returnsTaxable, 0 AS returnsTax
               FROM sales_invoices
              WHERE deleted_at IS NULL AND status = 'finalized' AND invoice_date BETWEEN ? AND ?
              GROUP BY invoice_date
             UNION ALL
             SELECT note_date, 0, 0, 0, 0, 0, SUM(total_paisa), SUM(taxable_paisa), SUM(tax_paisa)
               FROM credit_notes
              WHERE deleted_at IS NULL AND status = 'finalized' AND note_date BETWEEN ? AND ?
              GROUP BY note_date)
          GROUP BY date ORDER BY date`,
        [from, to, from, to],
      ),

    serverSales: (from: string, to: string): Promise<ServiceGroupSales[]> =>
      this.groupSales('COALESCE(o.server_id, o.created_by)', '', from, to),

    tableSales: (from: string, to: string): Promise<ServiceGroupSales[]> =>
      this.groupSales('o.table_id', `AND o.service_type = 'dine_in'`, from, to),

    daySales: (date: string): Promise<DaySale[]> =>
      this.driver.all<DaySale>(
        `SELECT i.finalized_at AS finalizedAt, i.total_paisa AS total,
                (SELECT o.service_type FROM orders o
                  WHERE o.invoice_id = i.id AND o.deleted_at IS NULL LIMIT 1) AS serviceType
           FROM sales_invoices i
          WHERE i.deleted_at IS NULL AND i.status = 'finalized' AND i.invoice_date = ?
          ORDER BY i.finalized_at`,
        [date],
      ),

    productSales: (from: string, to: string): Promise<ProductSales[]> =>
      this.driver.all<ProductSales>(
        `SELECT product_id AS productId, MAX(description) AS description, SUM(qty) AS qtyMilli,
                SUM(amount) AS amount, SUM(rqty) AS returnQtyMilli, SUM(ramount) AS returnAmount
           FROM (
             SELECT l.product_id, l.description, l.qty_milli AS qty, l.total_paisa AS amount,
                    0 AS rqty, 0 AS ramount
               FROM sales_invoice_lines l JOIN sales_invoices i ON i.id = l.invoice_id
              WHERE l.deleted_at IS NULL AND i.deleted_at IS NULL AND i.status = 'finalized'
                AND i.invoice_date BETWEEN ? AND ?
             UNION ALL
             SELECT l.product_id, l.description, 0, 0, l.qty_milli, l.total_paisa
               FROM credit_note_lines l JOIN credit_notes n ON n.id = l.credit_note_id
              WHERE l.deleted_at IS NULL AND n.deleted_at IS NULL AND n.status = 'finalized'
                AND n.note_date BETWEEN ? AND ?)
          GROUP BY COALESCE(product_id, 'text:' || description)`,
        [from, to, from, to],
      ),

    // Same balance rule as invoices.detail: total − active payments − finalized credit notes.
    openInvoices: (): Promise<OpenInvoice[]> =>
      this.driver.all<OpenInvoice>(`${OPEN_INVOICES} ORDER BY invoiceDate, number`),

    vatByDay: (from: string, to: string): Promise<VatDay[]> =>
      this.driver.all<VatDay>(
        `SELECT date, rateBp, SUM(taxable) AS taxable, SUM(tax) AS tax,
                SUM(rtaxable) AS returnsTaxable, SUM(rtax) AS returnsTax
           FROM (
             SELECT i.invoice_date AS date, l.tax_rate_bp AS rateBp, l.taxable_paisa AS taxable,
                    l.tax_paisa AS tax, 0 AS rtaxable, 0 AS rtax
               FROM sales_invoice_lines l JOIN sales_invoices i ON i.id = l.invoice_id
              WHERE l.deleted_at IS NULL AND i.deleted_at IS NULL AND i.status = 'finalized'
                AND i.invoice_date BETWEEN ? AND ?
             UNION ALL
             SELECT n.note_date, l.tax_rate_bp, 0, 0, l.taxable_paisa, l.tax_paisa
               FROM credit_note_lines l JOIN credit_notes n ON n.id = l.credit_note_id
              WHERE l.deleted_at IS NULL AND n.deleted_at IS NULL AND n.status = 'finalized'
                AND n.note_date BETWEEN ? AND ?)
          GROUP BY date, rateBp ORDER BY date, rateBp`,
        [from, to, from, to],
      ),

    profitSales: (from: string, to: string): Promise<ProductProfitSales[]> =>
      this.driver.all<ProductProfitSales>(
        `SELECT product_id AS productId, MAX(description) AS description, SUM(qty) AS qtyMilli,
                SUM(taxable) AS taxable, SUM(rqty) AS returnQtyMilli, SUM(rtaxable) AS returnTaxable,
                SUM(bqty) AS batchQtyMilli, CAST(ROUND(SUM(bcost)) AS INTEGER) AS batchCost
           FROM (
             SELECT l.product_id, l.description, l.qty_milli AS qty, l.taxable_paisa AS taxable,
                    0 AS rqty, 0 AS rtaxable,
                    CASE WHEN b.cost_paisa > 0 THEN l.qty_milli ELSE 0 END AS bqty,
                    CASE WHEN b.cost_paisa > 0 THEN l.qty_milli * b.cost_paisa / 1000.0 ELSE 0 END AS bcost
               FROM sales_invoice_lines l
               JOIN sales_invoices i ON i.id = l.invoice_id
               LEFT JOIN batches b ON b.id = l.batch_id AND b.deleted_at IS NULL
              WHERE l.deleted_at IS NULL AND i.deleted_at IS NULL AND i.status = 'finalized'
                AND i.invoice_date BETWEEN ? AND ?
                AND (l.product_id IS NOT NULL OR ${UNASSIGNED})
             UNION ALL
             SELECT l.product_id, l.description, 0, 0, l.qty_milli, l.taxable_paisa, 0, 0
               FROM credit_note_lines l JOIN credit_notes n ON n.id = l.credit_note_id
              WHERE l.deleted_at IS NULL AND n.deleted_at IS NULL AND n.status = 'finalized'
                AND n.note_date BETWEEN ? AND ?
                AND (l.product_id IS NOT NULL OR NOT EXISTS (
                  SELECT 1 FROM invoice_line_products a
                   WHERE a.invoice_line_id = l.invoice_line_id AND a.deleted_at IS NULL)))
          GROUP BY COALESCE(product_id, 'text:' || description)`,
        [from, to, from, to],
      ),

    profitSalesByDay: (from: string, to: string): Promise<ProductProfitDay[]> =>
      this.driver.all<ProductProfitDay>(
        `SELECT date, product_id AS productId, MAX(description) AS description, SUM(qty) AS qtyMilli,
                SUM(taxable) AS taxable, SUM(rqty) AS returnQtyMilli, SUM(rtaxable) AS returnTaxable
           FROM (
             SELECT i.invoice_date AS date, l.product_id, l.description, l.qty_milli AS qty,
                    l.taxable_paisa AS taxable, 0 AS rqty, 0 AS rtaxable
               FROM sales_invoice_lines l
               JOIN sales_invoices i ON i.id = l.invoice_id
              WHERE l.deleted_at IS NULL AND i.deleted_at IS NULL AND i.status = 'finalized'
                AND i.invoice_date BETWEEN ? AND ?
                AND (l.product_id IS NOT NULL OR ${UNASSIGNED})
             UNION ALL
             SELECT n.note_date, l.product_id, l.description, 0, 0, l.qty_milli, l.taxable_paisa
               FROM credit_note_lines l JOIN credit_notes n ON n.id = l.credit_note_id
              WHERE l.deleted_at IS NULL AND n.deleted_at IS NULL AND n.status = 'finalized'
                AND n.note_date BETWEEN ? AND ?
                AND (l.product_id IS NOT NULL OR NOT EXISTS (
                  SELECT 1 FROM invoice_line_products a
                   WHERE a.invoice_line_id = l.invoice_line_id AND a.deleted_at IS NULL)))
          GROUP BY date, COALESCE(product_id, 'text:' || description)`,
        [from, to, from, to],
      ),

    // The moving average, replayed from the ledger (packages/core/src/moving-cost.ts).
    costBasis: async (upTo: string | null): Promise<ProductCostBasis[]> => {
      const rows = await this.driver.all<{
        productId: string
        qtyMilli: number
        unitCostPaisa: number | null
        costed: number
      }>(
        `SELECT product_id AS productId, qty_milli AS qtyMilli, unit_cost_paisa AS unitCostPaisa,
                type IN ('receipt', 'opening') AS costed
           FROM stock_movements
          WHERE deleted_at IS NULL AND (? IS NULL OR date(occurred_at) <= ?)
          ORDER BY occurred_at, created_at, id`,
        [upTo, upTo],
      )
      const byProduct = new Map<string, CostMovement[]>()
      for (const r of rows) {
        const list = byProduct.get(r.productId) ?? []
        list.push({
          qtyMilli: r.qtyMilli,
          unitCostPaisa: r.unitCostPaisa ?? 0,
          costed: !!r.costed,
        })
        byProduct.set(r.productId, list)
      }
      return [...byProduct].flatMap(([productId, list]) => {
        const basis = movingCost(list)
        return basis ? [{ productId, ...basis }] : []
      })
    },

    // A product assigned to a custom line counts as sold on the bill's date.
    lastSales: (): Promise<LastSale[]> =>
      this.driver.all<LastSale>(
        `SELECT productId, MAX(date) AS date
           FROM (
             SELECT l.product_id AS productId, i.invoice_date AS date
               FROM sales_invoice_lines l JOIN sales_invoices i ON i.id = l.invoice_id
              WHERE l.deleted_at IS NULL AND i.deleted_at IS NULL AND i.status = 'finalized'
                AND l.product_id IS NOT NULL
             UNION ALL
             SELECT a.product_id, i.invoice_date
               FROM invoice_line_products a JOIN sales_invoices i ON i.id = a.invoice_id
              WHERE a.deleted_at IS NULL AND i.deleted_at IS NULL AND i.status = 'finalized')
          GROUP BY productId`,
      ),

    customSales: async (from: string, to: string): Promise<CustomSales> => {
      const rows = await this.driver.all<CustomSales>(
        `SELECT COUNT(*) AS lines, COALESCE(SUM(l.total_paisa), 0) AS amount,
                COALESCE(SUM(CASE WHEN ${UNASSIGNED} THEN 1 ELSE 0 END), 0) AS unassignedLines
           FROM sales_invoice_lines l JOIN sales_invoices i ON i.id = l.invoice_id
          WHERE l.deleted_at IS NULL AND i.deleted_at IS NULL AND i.status = 'finalized'
            AND l.product_id IS NULL AND i.invoice_date BETWEEN ? AND ?`,
        [from, to],
      )
      return rows[0] ?? { lines: 0, amount: 0, unassignedLines: 0 }
    },

    assignedCustom: async (from: string, to: string): Promise<AssignedCustom> => {
      const inRange = `l.deleted_at IS NULL AND i.deleted_at IS NULL AND i.status = 'finalized'
            AND l.product_id IS NULL AND i.invoice_date BETWEEN ? AND ?`
      const [lines, products] = await Promise.all([
        this.driver.all<AssignedCustom['lines'][number]>(
          `SELECT l.id AS lineId, i.invoice_date AS date, l.taxable_paisa AS taxable,
                  COALESCE((SELECT SUM(c.taxable_paisa)
                              FROM credit_note_lines c JOIN credit_notes n ON n.id = c.credit_note_id
                             WHERE c.invoice_line_id = l.id AND c.deleted_at IS NULL
                               AND n.deleted_at IS NULL AND n.status = 'finalized'), 0) AS returnTaxable
             FROM sales_invoice_lines l JOIN sales_invoices i ON i.id = l.invoice_id
            WHERE ${inRange} AND NOT ${UNASSIGNED}`,
          [from, to],
        ),
        this.driver.all<AssignedCustom['products'][number]>(
          `SELECT a.invoice_line_id AS lineId, a.product_id AS productId, a.qty_milli AS qtyMilli,
                  CASE WHEN b.cost_paisa > 0 THEN b.cost_paisa END AS batchCost
             FROM invoice_line_products a
             JOIN sales_invoice_lines l ON l.id = a.invoice_line_id
             JOIN sales_invoices i ON i.id = l.invoice_id
             LEFT JOIN batches b ON b.id = a.batch_id AND b.deleted_at IS NULL
            WHERE a.deleted_at IS NULL AND ${inRange}`,
          [from, to],
        ),
      ])
      return { lines, products }
    },
  }

  /** Receipts with their cost, for a WHERE clause over goods_receipts g. */
  private async receiptSummaries(
    where: string,
    params: SqlParam[],
    page?: { limit: number; offset: number },
  ): Promise<GoodsReceiptSummary[]> {
    const receipts = await this.rows(
      'goods_receipts',
      `SELECT g.* FROM goods_receipts g WHERE ${where}
        ORDER BY g.receipt_date DESC, g.created_at DESC${page ? ' LIMIT ? OFFSET ?' : ''}`,
      page ? [...params, page.limit, page.offset] : params,
    )
    if (receipts.length === 0) return []
    const totals = await this.driver.all<{ id: string; total: number; n: number }>(
      `SELECT goods_receipt_id AS id, COUNT(*) AS n,
              SUM(COALESCE(amount_paisa,
                           CAST(ROUND(qty_milli * unit_cost_paisa / 1000.0) AS INTEGER))) AS total
         FROM goods_receipt_lines
        WHERE deleted_at IS NULL AND goods_receipt_id IN (${receipts.map(() => '?').join(',')})
        GROUP BY goods_receipt_id`,
      receipts.map((r) => r.id),
    )
    const byId = new Map(totals.map((t) => [t.id, t]))
    return receipts.map((receipt) => ({
      receipt,
      totalPaisa: Math.max(0, (byId.get(receipt.id)?.total ?? 0) - receipt.discount_paisa),
      lineCount: byId.get(receipt.id)?.n ?? 0,
    }))
  }

  readonly purchases = {
    orders: async (filter: PurchaseOrderFilter = {}): Promise<Page<PurchaseOrderRow>> => {
      const where = ['deleted_at IS NULL']
      const params: SqlParam[] = []
      if (filter.status) {
        where.push('status = ?')
        params.push(filter.status)
      }
      if (filter.supplierId) {
        where.push('supplier_id = ?')
        params.push(filter.supplierId)
      }
      if (filter.search?.trim()) {
        where.push(`number LIKE ? ESCAPE '\\'`)
        params.push(`%${escapeLike(filter.search.trim())}%`)
      }
      const base = `SELECT * FROM purchase_orders WHERE ${where.join(' AND ')}`
      const [rows, total] = await Promise.all([
        this.rows(
          'purchase_orders',
          `${base} ORDER BY order_date DESC, created_at DESC LIMIT ? OFFSET ?`,
          [...params, filter.limit ?? DEFAULT_LIMIT, filter.offset ?? 0],
        ),
        this.count(base, params),
      ])
      return { rows, total }
    },

    orderBoard: async (filter: PurchaseOrderBoardFilter): Promise<Page<PurchaseOrderBoardRow>> => {
      const where = ['1 = 1']
      const params: SqlParam[] = []
      if (filter.show === 'open') where.push(PO_OPEN)
      if (filter.show === 'finished') where.push(`NOT ${PO_OPEN}`)
      if (filter.state === 'draft') where.push(`status = 'draft'`)
      if (filter.state === 'waiting') where.push(`status = 'submitted'`)
      if (filter.state === 'ready') where.push(`status IN ('issued', 'approved')`)
      if (filter.state === 'sent') where.push(`status = 'sent' AND _received = 0 AND _left > 0`)
      if (filter.state === 'part') where.push(`status = 'sent' AND _received > 0 AND _left > 0`)
      if (filter.supplierId) {
        where.push('supplier_id = ?')
        params.push(filter.supplierId)
      }
      if (filter.search?.trim()) {
        const term = `%${escapeLike(filter.search.trim())}%`
        where.push(`(number LIKE ? ESCAPE '\\' OR _supplier LIKE ? ESCAPE '\\')`)
        params.push(term, term)
      }
      const base = `SELECT * FROM (${PO_BOARD}) WHERE ${where.join(' AND ')}`
      // Open: late first, then the soonest needed, then drafts. Otherwise newest first.
      const order =
        filter.show === 'open'
          ? `CASE WHEN status = 'draft' THEN 2 WHEN expected_date < ? THEN 0 ELSE 1 END,
             expected_date IS NULL, COALESCE(expected_date, order_date), created_at DESC`
          : 'order_date DESC, created_at DESC'
      const [found, total] = await Promise.all([
        this.driver.all<SqlRow>(`${base} ORDER BY ${order} LIMIT ? OFFSET ?`, [
          ...params,
          ...(filter.show === 'open' ? [filter.today] : []),
          filter.limit ?? DEFAULT_LIMIT,
          filter.offset ?? 0,
        ]),
        this.count(base, params),
      ])
      const rows = found.map((r) => {
        const { _supplier, _lines, _ordered, _received, _left, _due, ...row } = r as Record<
          string,
          SqliteValue
        >
        void _left
        return {
          order: decodeRow('purchase_orders', row) as PurchaseOrderRow,
          supplierName: (_supplier as string | null) ?? null,
          lineCount: Number(_lines),
          orderedMilli: Number(_ordered),
          receivedMilli: Number(_received),
          duePaisa: Number(_due),
        }
      })
      return { rows, total }
    },

    orderCounts: async (today: string): Promise<PurchaseOrderCounts> => {
      const [c] = await this.driver.all<PurchaseOrderCounts>(
        `SELECT COALESCE(SUM(status = 'draft'), 0) AS draft,
                COALESCE(SUM(status = 'submitted'), 0) AS waiting,
                COALESCE(SUM(status = 'approved'), 0) AS approved,
                COALESCE(SUM(status IN ('issued', 'approved')), 0) AS ready,
                COALESCE(SUM(status = 'sent' AND _received = 0 AND _left > 0), 0) AS sent,
                COALESCE(SUM(status = 'sent' AND _received > 0 AND _left > 0), 0) AS part,
                COALESCE(SUM(NOT ${PO_OPEN}), 0) AS finished,
                COALESCE(SUM(status IN ('issued', 'sent') AND _left > 0 AND expected_date < ?), 0) AS late,
                COALESCE(SUM(CASE WHEN status IN ('issued', 'sent') THEN _due ELSE 0 END), 0) AS duePaisa
           FROM (${PO_BOARD})`,
        [today],
      )
      return c!
    },

    /**
     * The sidebar's Approvals and Purchase requests: orders and requests waiting for
     * approval, whether the business has approval steps at all, and approved requests with
     * something left to order. One statement: the shell runs it on every screen.
     */
    approvalSummary: async (): Promise<{
      waiting: number
      rules: number
      requestsToOrder: number
    }> => {
      const [c] = await this.driver.all<{ waiting: number; rules: number; requests: number }>(
        `SELECT (SELECT COUNT(*) FROM purchase_orders WHERE status = 'submitted' AND deleted_at IS NULL)
              + (SELECT COUNT(*) FROM purchase_requests WHERE status = 'submitted' AND deleted_at IS NULL) AS waiting,
                (SELECT COUNT(*) FROM approval_rules WHERE deleted_at IS NULL) AS rules,
                (SELECT COUNT(DISTINCT l.purchase_request_id)
                   FROM purchase_request_lines l
                   JOIN purchase_requests r ON r.id = l.purchase_request_id
                   LEFT JOIN (${REQ_ORDERED}) o ON o.line_id = l.id
                  WHERE r.status = 'approved' AND r.deleted_at IS NULL AND l.deleted_at IS NULL
                    AND l.qty_milli > COALESCE(o.qty, 0)) AS requests`,
      )
      return {
        waiting: Number(c?.waiting ?? 0),
        rules: Number(c?.rules ?? 0),
        requestsToOrder: Number(c?.requests ?? 0),
      }
    },

    toOrder: async (filter: { locationId?: string } = {}): Promise<ToOrderRow[]> => {
      const at = filter.locationId ?? null
      const found = await this.driver.all<SqlRow>(
        `WITH st AS (
           SELECT product_id, SUM(qty_milli) AS qty FROM current_stock
            WHERE ? IS NULL OR location_id = ? GROUP BY product_id),
         oo AS (
           SELECT l.product_id, SUM(MAX(0, l.qty_ordered_milli - COALESCE((
                    SELECT SUM(r.qty_milli) FROM goods_receipt_lines r
                     WHERE r.purchase_order_line_id = l.id AND r.deleted_at IS NULL), 0))) AS qty
             FROM purchase_order_lines l JOIN purchase_orders o ON o.id = l.purchase_order_id
            WHERE o.status IN ('draft', 'submitted', 'approved', 'issued', 'sent')
              AND o.deleted_at IS NULL AND l.deleted_at IS NULL
              AND (? IS NULL OR o.location_id = ? OR o.location_id IS NULL)
            GROUP BY l.product_id)
         SELECT p.*, COALESCE(st.qty, 0) AS _stock, COALESCE(oo.qty, 0) AS _on_order
           FROM products p
           LEFT JOIN st ON st.product_id = p.id
           LEFT JOIN oo ON oo.product_id = p.id
          WHERE p.deleted_at IS NULL AND p.is_active = 1 AND p.kind <> 'group' AND p.track_stock = 1
            AND (p.min_stock_milli > 0 OR p.reorder_qty_milli > 0)
            AND COALESCE(st.qty, 0) + COALESCE(oo.qty, 0) <= p.min_stock_milli
          ORDER BY p.name`,
        [at, at, at, at],
      )
      return found.map((r) => {
        const { _stock, _on_order, ...row } = r as Record<string, SqliteValue>
        return {
          product: decodeRow('products', row) as ProductRow,
          stockMilli: Number(_stock),
          onOrderMilli: Number(_on_order),
        }
      })
    },

    requestBoard: async (
      filter: PurchaseRequestBoardFilter,
    ): Promise<Page<PurchaseRequestBoardRow>> => {
      const where = ['1 = 1']
      const params: SqlParam[] = []
      if (filter.show === 'open') where.push(REQ_OPEN)
      if (filter.show === 'finished') where.push(`NOT ${REQ_OPEN}`)
      if (filter.state === 'draft') where.push(`status = 'draft'`)
      if (filter.state === 'waiting') where.push(`status = 'submitted'`)
      if (filter.state === 'toOrder') where.push(`status = 'approved' AND _started = 0`)
      if (filter.state === 'part')
        where.push(`status = 'approved' AND _started > 0 AND _done < _lines`)
      if (filter.search?.trim()) {
        const term = `%${escapeLike(filter.search.trim())}%`
        where.push(`(number LIKE ? ESCAPE '\\' OR id IN (
          SELECT l.purchase_request_id FROM purchase_request_lines l
            LEFT JOIN products p ON p.id = l.product_id
           WHERE l.deleted_at IS NULL
             AND (p.name LIKE ? ESCAPE '\\' OR l.description LIKE ? ESCAPE '\\')))`)
        params.push(term, term, term)
      }
      const base = `SELECT * FROM (${REQ_BOARD}) WHERE ${where.join(' AND ')}`
      // Open: ready to order first, then waiting, then drafts; the soonest needed first.
      const order =
        filter.show === 'open'
          ? `CASE status WHEN 'approved' THEN 0 WHEN 'submitted' THEN 1 ELSE 2 END,
             needed_by IS NULL, needed_by, created_at`
          : 'COALESCE(closed_at, updated_at) DESC, created_at DESC'
      const [found, total] = await Promise.all([
        this.driver.all<SqlRow>(`${base} ORDER BY ${order} LIMIT ? OFFSET ?`, [
          ...params,
          filter.limit ?? DEFAULT_LIMIT,
          filter.offset ?? 0,
        ]),
        this.count(base, params),
      ])
      const rows = found.map((r) => {
        const { _location, _lines, _done, _started, ...row } = r as Record<string, SqliteValue>
        return {
          request: decodeRow('purchase_requests', row) as PurchaseRequestRow,
          locationName: (_location as string | null) ?? null,
          lineCount: Number(_lines),
          doneLines: Number(_done),
          startedLines: Number(_started),
        }
      })
      return { rows, total }
    },

    requestCounts: async (today: string): Promise<PurchaseRequestCounts> => {
      const [c] = await this.driver.all<PurchaseRequestCounts>(
        `SELECT COALESCE(SUM(status = 'draft'), 0) AS draft,
                COALESCE(SUM(status = 'submitted'), 0) AS waiting,
                COALESCE(SUM(status = 'approved' AND _started = 0 AND _lines > 0), 0) AS toOrder,
                COALESCE(SUM(status = 'approved' AND _started > 0 AND _done < _lines), 0) AS part,
                COALESCE(SUM(NOT ${REQ_OPEN}), 0) AS finished,
                COALESCE(SUM(${REQ_OPEN} AND needed_by < ?), 0) AS late
           FROM (${REQ_BOARD})`,
        [today],
      )
      return c!
    },

    requestDetail: async (id: string): Promise<PurchaseRequestDetail | null> => {
      const request = await this.get('purchase_requests', id)
      if (!request) return null
      const lines = await this.rows(
        'purchase_request_lines',
        `SELECT * FROM purchase_request_lines WHERE purchase_request_id = ? AND deleted_at IS NULL ORDER BY line_no`,
        [id],
      )
      const productIds = [...new Set(lines.flatMap((l) => (l.product_id ? [l.product_id] : [])))]
      const [products, refs] = await Promise.all([
        productIds.length
          ? this.rows(
              'products',
              `SELECT * FROM products WHERE id IN (${placeholders(productIds)})`,
              productIds,
            )
          : Promise.resolve([]),
        this.driver.all<{
          line_id: string
          order_id: string
          number: string | null
          status: RequestOrderRef['status']
          supplier: string | null
          qty: number
        }>(
          `SELECT pol.purchase_request_line_id AS line_id, po.id AS order_id, po.number, po.status,
                  s.name AS supplier, SUM(pol.qty_ordered_milli) AS qty
             FROM purchase_order_lines pol
             JOIN purchase_orders po ON po.id = pol.purchase_order_id
             LEFT JOIN suppliers s ON s.id = po.supplier_id
            WHERE pol.deleted_at IS NULL AND po.deleted_at IS NULL
              AND po.status NOT IN ('cancelled', 'rejected')
              AND pol.purchase_request_line_id IN (
                SELECT id FROM purchase_request_lines WHERE purchase_request_id = ?)
            GROUP BY pol.purchase_request_line_id, po.id
            ORDER BY po.created_at`,
          [id],
        ),
      ])
      const orderedByLine: Record<string, number> = {}
      const ordersByLine: Record<string, RequestOrderRef[]> = {}
      for (const r of refs) {
        orderedByLine[r.line_id] = (orderedByLine[r.line_id] ?? 0) + Number(r.qty)
        ;(ordersByLine[r.line_id] ??= []).push({
          orderId: r.order_id,
          number: r.number,
          status: r.status,
          supplierName: r.supplier,
          qtyMilli: Number(r.qty),
        })
      }
      return {
        request,
        lines,
        products: Object.fromEntries(products.map((p) => [p.id, p])),
        orderedByLine,
        ordersByLine,
      }
    },

    requestLinesLeft: async (requestIds: readonly string[]): Promise<RequestLineLeft[]> => {
      const only = requestIds.length ? `AND r.id IN (${placeholders([...requestIds])})` : ''
      const found = await this.driver.all<SqlRow>(
        `SELECT l.*, r.number AS _number, r.location_id AS _location,
                MAX(0, l.qty_milli - COALESCE(o.qty, 0)) AS _left
           FROM purchase_request_lines l
           JOIN purchase_requests r ON r.id = l.purchase_request_id
           LEFT JOIN (${REQ_ORDERED}) o ON o.line_id = l.id
          WHERE l.deleted_at IS NULL AND r.deleted_at IS NULL AND r.status = 'approved'
            AND l.qty_milli > COALESCE(o.qty, 0) ${only}
          ORDER BY r.created_at, l.line_no`,
        [...requestIds],
      )
      const productIds = [
        ...new Set(found.flatMap((r) => (r.product_id ? [r.product_id as string] : []))),
      ]
      const products = productIds.length
        ? await this.rows(
            'products',
            `SELECT * FROM products WHERE id IN (${placeholders(productIds)})`,
            productIds,
          )
        : []
      const byId = new Map(products.map((p) => [p.id, p]))
      return found.map((r) => {
        const { _number, _location, _left, ...row } = r as Record<string, SqliteValue>
        const line = decodeRow('purchase_request_lines', row) as RequestLineLeft['line']
        return {
          requestId: line.purchase_request_id,
          requestNumber: (_number as string | null) ?? null,
          locationId: (_location as string | null) ?? null,
          line,
          product: line.product_id ? (byId.get(line.product_id) ?? null) : null,
          leftMilli: Number(_left),
        }
      })
    },

    orderDetail: async (id: string): Promise<PurchaseOrderDetail | null> => {
      const order = await this.get('purchase_orders', id)
      if (!order) return null
      const [lines, received, receipts] = await Promise.all([
        this.rows(
          'purchase_order_lines',
          `SELECT * FROM purchase_order_lines WHERE purchase_order_id = ? AND deleted_at IS NULL ORDER BY line_no`,
          [id],
        ),
        this.driver.all<{ line_id: string; qty: number }>(
          `SELECT l.purchase_order_line_id AS line_id, SUM(l.qty_milli) AS qty
             FROM goods_receipt_lines l JOIN goods_receipts g ON g.id = l.goods_receipt_id
            WHERE g.purchase_order_id = ? AND g.deleted_at IS NULL AND l.deleted_at IS NULL
              AND l.purchase_order_line_id IS NOT NULL
            GROUP BY l.purchase_order_line_id`,
          [id],
        ),
        this.receiptSummaries('g.purchase_order_id = ? AND g.deleted_at IS NULL', [id]),
      ])
      return {
        order,
        lines,
        receivedByLine: Object.fromEntries(received.map((r) => [r.line_id, r.qty])),
        receipts,
      }
    },

    receipts: async (filter: GoodsReceiptFilter = {}): Promise<Page<GoodsReceiptSummary>> => {
      const where = ['g.deleted_at IS NULL']
      const params: SqlParam[] = []
      if (filter.supplierId) {
        where.push('g.supplier_id = ?')
        params.push(filter.supplierId)
      }
      if (filter.from) {
        where.push('g.receipt_date >= ?')
        params.push(filter.from)
      }
      if (filter.to) {
        where.push('g.receipt_date <= ?')
        params.push(filter.to)
      }
      if (filter.search?.trim()) {
        where.push(`g.reference LIKE ? ESCAPE '\\'`)
        params.push(`%${escapeLike(filter.search.trim())}%`)
      }
      if (filter.bill === 'none') {
        where.push(`NOT EXISTS (${LIVE_BILL} )`)
      } else if (filter.bill) {
        const cmp = filter.bill === 'paid' ? '<=' : '>'
        const late = filter.bill === 'late' ? ' AND b.due_date < ?' : ''
        where.push(`EXISTS (${LIVE_BILL} AND b.total_paisa ${cmp} ${COVERED}${late})`)
        if (filter.bill === 'late') params.push(filter.today ?? '9999-12-31')
      }
      const clause = where.join(' AND ')
      const [rows, total] = await Promise.all([
        this.receiptSummaries(clause, params, {
          limit: filter.limit ?? DEFAULT_LIMIT,
          offset: filter.offset ?? 0,
        }),
        this.count(`SELECT g.id FROM goods_receipts g WHERE ${clause}`, params),
      ])
      return { rows, total }
    },

    lastReceiptLine: async (productId: string, supplierId: string | null) => {
      const rows = await this.rows(
        'goods_receipt_lines',
        `SELECT l.* FROM goods_receipt_lines l JOIN goods_receipts g ON g.id = l.goods_receipt_id
          WHERE l.product_id = ? AND l.deleted_at IS NULL AND g.deleted_at IS NULL
            AND (? IS NULL OR g.supplier_id = ?)
          ORDER BY g.receipt_date DESC, g.created_at DESC LIMIT 1`,
        [productId, supplierId, supplierId],
      )
      return rows[0] ?? null
    },
    receiptDetail: async (id: string): Promise<GoodsReceiptDetail | null> => {
      const receipt = await this.get('goods_receipts', id)
      if (!receipt) return null
      const lines = await this.rows(
        'goods_receipt_lines',
        `SELECT * FROM goods_receipt_lines WHERE goods_receipt_id = ? AND deleted_at IS NULL ORDER BY line_no`,
        [id],
      )
      return { receipt, lines }
    },
  }

  readonly payables: PayablesRepository = {
    bills: async (filter: SupplierBillFilter = {}): Promise<SupplierBillSummary[]> => {
      const where = [`b.deleted_at IS NULL AND b.status = 'active'`]
      const params: SqlParam[] = []
      if (filter.supplierId) {
        where.push('b.supplier_id = ?')
        params.push(filter.supplierId)
      }
      if (filter.receiptIds) {
        if (filter.receiptIds.length === 0) return []
        where.push(`b.goods_receipt_id IN (${filter.receiptIds.map(() => '?').join(',')})`)
        params.push(...filter.receiptIds)
      }
      if (filter.open) where.push(`b.total_paisa > ${COVERED}`)
      const rows = await this.driver.all<SqlRow>(
        `SELECT b.*, ${COVERED} AS covered_paisa, s.name AS supplier_name
           FROM supplier_bills b LEFT JOIN suppliers s ON s.id = b.supplier_id
          WHERE ${where.join(' AND ')}
          ORDER BY COALESCE(b.due_date, b.bill_date), b.created_at`,
        params,
      )
      return rows.map((r) => this.billSummary(r))
    },

    billDetail: async (id: string): Promise<SupplierBillDetail | null> => {
      const rows = await this.driver.all<SqlRow>(
        `SELECT b.*, ${COVERED} AS covered_paisa, s.name AS supplier_name
           FROM supplier_bills b LEFT JOIN suppliers s ON s.id = b.supplier_id
          WHERE b.id = ? AND b.deleted_at IS NULL`,
        [id],
      )
      if (!rows[0]) return null
      const [payments, credits, shares] = await Promise.all([
        this.rows(
          'supplier_payments',
          `SELECT p.* FROM supplier_payments p WHERE p.deleted_at IS NULL AND p.id IN
             (SELECT payment_id FROM supplier_allocations WHERE bill_id = ? AND deleted_at IS NULL)
           ORDER BY p.payment_date DESC, p.created_at DESC`,
          [id],
        ),
        this.rows(
          'supplier_credits',
          `SELECT c.* FROM supplier_credits c WHERE c.deleted_at IS NULL AND c.id IN
             (SELECT credit_id FROM supplier_allocations WHERE bill_id = ? AND deleted_at IS NULL)
           ORDER BY c.credit_date DESC, c.created_at DESC`,
          [id],
        ),
        this.driver.all<{ source: string; amount: number }>(
          `SELECT COALESCE(payment_id, credit_id) AS source, SUM(amount_paisa) AS amount
             FROM supplier_allocations WHERE bill_id = ? AND deleted_at IS NULL
            GROUP BY COALESCE(payment_id, credit_id)`,
          [id],
        ),
      ])
      const share = new Map(shares.map((x) => [x.source, x.amount]))
      return {
        ...this.billSummary(rows[0]),
        payments: payments.map((payment) => ({ payment, amountPaisa: share.get(payment.id) ?? 0 })),
        credits: credits.map((credit) => ({ credit, amountPaisa: share.get(credit.id) ?? 0 })),
      }
    },

    payments: async (filter = {}): Promise<SupplierPaymentSummary[]> => {
      const where = ['p.deleted_at IS NULL']
      const params: SqlParam[] = []
      if (filter.supplierId) {
        where.push('p.supplier_id = ?')
        params.push(filter.supplierId)
      }
      if (filter.from) {
        where.push('p.payment_date >= ?')
        params.push(filter.from)
      }
      if (filter.to) {
        where.push('p.payment_date <= ?')
        params.push(filter.to)
      }
      const rows = await this.driver.all<SqlRow>(
        `SELECT p.*, s.name AS supplier_name,
                (SELECT COALESCE(SUM(a.amount_paisa), 0) FROM supplier_allocations a
                  WHERE a.payment_id = p.id AND a.deleted_at IS NULL) AS allocated_paisa,
                (SELECT GROUP_CONCAT(COALESCE(NULLIF(b.bill_no, ''), 'Bill'), '\u001f')
                   FROM supplier_allocations a JOIN supplier_bills b ON b.id = a.bill_id
                  WHERE a.payment_id = p.id AND a.deleted_at IS NULL) AS bill_nos
           FROM supplier_payments p LEFT JOIN suppliers s ON s.id = p.supplier_id
          WHERE ${where.join(' AND ')}
          ORDER BY p.payment_date DESC, p.created_at DESC`,
        params,
      )
      return rows.map((r) => {
        const { supplier_name, allocated_paisa, bill_nos, ...row } = r as Record<
          string,
          SqliteValue
        >
        return {
          payment: decodeRow('supplier_payments', row) as RowOf<'supplier_payments'>,
          supplierName: (supplier_name as string | null) ?? null,
          allocatedPaisa: Number(allocated_paisa ?? 0),
          bills: bill_nos ? String(bill_nos).split('\u001f') : [],
        }
      })
    },

    credits: async (filter = {}): Promise<SupplierCreditSummary[]> => {
      const rows = await this.driver.all<SqlRow>(
        `SELECT c.*,
                (SELECT COALESCE(SUM(a.amount_paisa), 0) FROM supplier_allocations a
                  WHERE a.credit_id = c.id AND a.deleted_at IS NULL) AS allocated_paisa,
                (SELECT COUNT(*) FROM supplier_credit_lines l
                  WHERE l.supplier_credit_id = c.id AND l.deleted_at IS NULL) AS line_count,
                (SELECT GROUP_CONCAT(COALESCE(NULLIF(b.bill_no, ''), 'Bill'), '\u001f')
                   FROM supplier_allocations a JOIN supplier_bills b ON b.id = a.bill_id
                  WHERE a.credit_id = c.id AND a.deleted_at IS NULL) AS bill_nos
           FROM supplier_credits c
          WHERE c.deleted_at IS NULL${filter.supplierId ? ' AND c.supplier_id = ?' : ''}
          ORDER BY c.credit_date DESC, c.created_at DESC`,
        filter.supplierId ? [filter.supplierId] : [],
      )
      return rows.map((r) => {
        const { allocated_paisa, line_count, bill_nos, ...row } = r as Record<string, SqliteValue>
        return {
          credit: decodeRow('supplier_credits', row) as RowOf<'supplier_credits'>,
          allocatedPaisa: Number(allocated_paisa ?? 0),
          lineCount: Number(line_count ?? 0),
          bills: bill_nos ? String(bill_nos).split('\u001f') : [],
        }
      })
    },

    creditMovements: (creditId: string): Promise<MovementRow[]> =>
      this.rows(
        'stock_movements',
        `SELECT * FROM stock_movements
          WHERE ref_type = 'supplier_credit' AND ref_id = ? AND deleted_at IS NULL`,
        [creditId],
      ),

    owed: async (): Promise<SupplierOwed[]> => {
      const rows = await this.driver.all<{
        id: string
        name: string
        billed: number
        covered: number
        open_bills: number
        given: number
        used: number
      }>(
        `SELECT s.id, s.name,
                COALESCE((SELECT SUM(b.total_paisa) FROM supplier_bills b
                           WHERE b.supplier_id = s.id AND ${LIVE}), 0) AS billed,
                COALESCE((SELECT SUM(${COVERED}) FROM supplier_bills b
                           WHERE b.supplier_id = s.id AND ${LIVE}), 0) AS covered,
                (SELECT COUNT(*) FROM supplier_bills b
                  WHERE b.supplier_id = s.id AND ${LIVE} AND b.total_paisa > ${COVERED}) AS open_bills,
                COALESCE((SELECT SUM(p.amount_paisa) FROM supplier_payments p
                           WHERE p.supplier_id = s.id AND p.deleted_at IS NULL AND p.status = 'active'), 0)
                + COALESCE((SELECT SUM(c.total_paisa) FROM supplier_credits c
                             WHERE c.supplier_id = s.id AND c.deleted_at IS NULL AND c.status = 'active'), 0) AS given,
                COALESCE((SELECT SUM(a.amount_paisa) FROM supplier_allocations a
                           LEFT JOIN supplier_payments p ON p.id = a.payment_id
                           LEFT JOIN supplier_credits c ON c.id = a.credit_id
                          WHERE a.deleted_at IS NULL AND COALESCE(p.status, c.status) = 'active'
                            AND COALESCE(p.supplier_id, c.supplier_id) = s.id), 0) AS used
           FROM suppliers s WHERE s.deleted_at IS NULL ORDER BY s.name`,
      )
      return rows
        .map((r) => ({
          supplierId: r.id,
          supplierName: r.name,
          owedPaisa: r.billed - r.covered,
          advancePaisa: Math.max(0, r.given - r.used),
          openBills: r.open_bills,
        }))
        .filter((r) => r.owedPaisa > 0 || r.advancePaisa > 0)
    },

    totals: async (today: string) => {
      const rows = await this.driver.all<{ owed: number; overdue: number; late: number }>(
        `SELECT COALESCE(SUM(owed), 0) AS owed,
                COALESCE(SUM(CASE WHEN due < ? THEN owed ELSE 0 END), 0) AS overdue,
                COALESCE(SUM(CASE WHEN due < ? THEN 1 ELSE 0 END), 0) AS late
           FROM (SELECT b.total_paisa - ${COVERED} AS owed, b.due_date AS due
                   FROM supplier_bills b WHERE ${LIVE}) x
          WHERE owed > 0`,
        [today, today],
      )
      const r = rows[0]
      return { owedPaisa: r?.owed ?? 0, overduePaisa: r?.overdue ?? 0, lateBills: r?.late ?? 0 }
    },
  }

  readonly parties: PartiesRepository = {
    customers: (filter) =>
      this.partyBoard('customers', filter) as Promise<
        Page<{ party: RowOf<'customers'> } & PartyStanding>
      >,
    suppliers: (filter) =>
      this.partyBoard('suppliers', filter) as Promise<
        Page<{ party: RowOf<'suppliers'> } & PartyStanding>
      >,
    summary: async (kind, today) => {
      const rows = await this.driver.all<{ owed: number; owing: number; late: number }>(
        `SELECT COALESCE(SUM(owed), 0) AS owed, COUNT(*) AS owing,
                COALESCE(SUM(CASE WHEN late > 0 THEN 1 ELSE 0 END), 0) AS late
           FROM (${PARTY_OPEN[kind]}) o
           JOIN ${kind} p ON p.id = o.pid AND p.deleted_at IS NULL
          WHERE o.owed > 0`,
        [today],
      )
      const r = rows[0]
      return { owedPaisa: r?.owed ?? 0, owing: r?.owing ?? 0, late: r?.late ?? 0 }
    },
    customerLedger: async (customerId) => {
      // Same rules as invoices.detail: active payments' live allocations, finalized credit notes.
      const [bills, payments, allocations, credits] = await Promise.all([
        this.driver.all<{
          id: string
          number: string | null
          date: string
          dueDate: string | null
          createdAt: string
          totalPaisa: number
          paidPaisa: number
          creditedPaisa: number
        }>(
          `SELECT i.id, i.number, i.invoice_date AS date, i.due_date AS dueDate,
                  i.created_at AS createdAt, i.total_paisa AS totalPaisa,
                  COALESCE((SELECT SUM(a.amount_paisa) FROM payment_allocations a
                              JOIN payments p ON p.id = a.payment_id
                             WHERE a.invoice_id = i.id AND a.deleted_at IS NULL
                               AND p.status = 'active' AND p.deleted_at IS NULL), 0) AS paidPaisa,
                  COALESCE((SELECT SUM(c.total_paisa) FROM credit_notes c
                             WHERE c.invoice_id = i.id AND c.status = 'finalized'
                               AND c.deleted_at IS NULL), 0) AS creditedPaisa
             FROM sales_invoices i
            WHERE i.customer_id = ? AND i.status = 'finalized' AND i.deleted_at IS NULL
            ORDER BY i.invoice_date, i.created_at`,
          [customerId],
        ),
        this.driver.all<{
          id: string
          date: string
          createdAt: string
          method: PaymentMethod
          reference: string | null
          amountPaisa: number
        }>(
          `SELECT id, payment_date AS date, created_at AS createdAt, method, reference,
                  amount_paisa AS amountPaisa
             FROM payments
            WHERE customer_id = ? AND status = 'active' AND deleted_at IS NULL
            ORDER BY payment_date, created_at`,
          [customerId],
        ),
        this.driver.all<{
          paymentId: string
          invoiceId: string
          number: string | null
          amountPaisa: number
        }>(
          `SELECT a.payment_id AS paymentId, a.invoice_id AS invoiceId, i.number,
                  a.amount_paisa AS amountPaisa
             FROM payment_allocations a
             JOIN payments p ON p.id = a.payment_id
             JOIN sales_invoices i ON i.id = a.invoice_id
            WHERE p.customer_id = ? AND p.status = 'active' AND p.deleted_at IS NULL
              AND a.deleted_at IS NULL AND i.status = 'finalized' AND i.deleted_at IS NULL
            ORDER BY i.invoice_date, i.created_at`,
          [customerId],
        ),
        this.driver.all<{
          id: string
          number: string | null
          date: string
          createdAt: string
          totalPaisa: number
          invoiceId: string
          invoiceNumber: string | null
          reason: string
        }>(
          `SELECT c.id, c.number, c.note_date AS date, c.created_at AS createdAt,
                  c.total_paisa AS totalPaisa, c.invoice_id AS invoiceId,
                  i.number AS invoiceNumber, c.reason
             FROM credit_notes c
             JOIN sales_invoices i ON i.id = c.invoice_id
            WHERE i.customer_id = ? AND c.status = 'finalized' AND c.deleted_at IS NULL
            ORDER BY c.note_date, c.created_at`,
          [customerId],
        ),
      ])
      const byPayment = new Map<string, CustomerLedger['payments'][number]['allocations']>()
      for (const a of allocations) {
        const list = byPayment.get(a.paymentId) ?? []
        list.push({ invoiceId: a.invoiceId, number: a.number, amountPaisa: a.amountPaisa })
        byPayment.set(a.paymentId, list)
      }
      return {
        bills,
        payments: payments.map((p) => ({ ...p, allocations: byPayment.get(p.id) ?? [] })),
        credits,
      }
    },
  }

  /** One page of customers or suppliers by name, each with what is owed and their last bill. */
  private async partyBoard(
    kind: 'customers' | 'suppliers',
    filter: PartyBoardFilter,
  ): Promise<Page<{ party: RowOf<'customers'> | RowOf<'suppliers'> } & PartyStanding>> {
    const where = ['p.deleted_at IS NULL']
    const params: SqlParam[] = [filter.today]
    const term = filter.search?.trim()
    if (term) {
      const like = `%${escapeLike(term)}%`
      where.push(
        `(p.name LIKE ? ESCAPE '\\' OR p.phone LIKE ? ESCAPE '\\' OR p.email LIKE ? ESCAPE '\\'
          OR p.pan_vat_number LIKE ? ESCAPE '\\')`,
      )
      params.push(like, like, like, like)
    }
    if (filter.show === 'owing') where.push('COALESCE(o.owed, 0) > 0')
    if (filter.show === 'inactive') where.push('p.is_active = 0')
    const base = `SELECT p.*, COALESCE(o.owed, 0) AS ps_owed, COALESCE(o.bills, 0) AS ps_bills,
                         COALESCE(o.late, 0) AS ps_late, o.oldest AS ps_oldest,
                         o.first_due AS ps_first_due, l.d AS ps_last_date, l.t AS ps_last_total
                    FROM ${kind} p
                    LEFT JOIN (${PARTY_OPEN[kind]}) o ON o.pid = p.id
                    LEFT JOIN (${PARTY_LAST[kind]}) l ON l.pid = p.id AND l.rn = 1
                   WHERE ${where.join(' AND ')}`
    const [raw, total] = await Promise.all([
      this.driver.all<SqlRow>(`${base} ORDER BY p.name COLLATE NOCASE, p.id LIMIT ? OFFSET ?`, [
        ...params,
        filter.limit ?? DEFAULT_LIMIT,
        filter.offset ?? 0,
      ]),
      this.count(base, params),
    ])
    const rows = raw.map((r) => {
      const {
        ps_owed,
        ps_bills,
        ps_late,
        ps_oldest,
        ps_first_due,
        ps_last_date,
        ps_last_total,
        ...row
      } = r as Record<string, SqliteValue>
      return {
        party: decodeRow(kind, row) as RowOf<'customers'> | RowOf<'suppliers'>,
        owedPaisa: Number(ps_owed ?? 0),
        openBills: Number(ps_bills ?? 0),
        lateBills: Number(ps_late ?? 0),
        oldestOpenDate: (ps_oldest as string | null) ?? null,
        firstDueDate: (ps_first_due as string | null) ?? null,
        lastDate: (ps_last_date as string | null) ?? null,
        lastTotalPaisa: ps_last_total == null ? null : Number(ps_last_total),
      }
    })
    return { rows, total }
  }

  private billSummary(r: SqlRow): SupplierBillSummary {
    const { covered_paisa, supplier_name, ...row } = r as Record<string, SqliteValue>
    return {
      bill: decodeRow('supplier_bills', row) as RowOf<'supplier_bills'>,
      coveredPaisa: Number(covered_paisa ?? 0),
      supplierName: (supplier_name as string | null) ?? null,
    }
  }

  readonly salesOrders = {
    detail: async (id: string): Promise<SalesOrderDetail | null> => {
      const order = await this.get('sales_orders', id)
      if (!order) return null
      const [lines, invoiced, invoices] = await Promise.all([
        this.rows(
          'sales_order_lines',
          `SELECT * FROM sales_order_lines WHERE sales_order_id = ? AND deleted_at IS NULL ORDER BY line_no`,
          [id],
        ),
        this.driver.all<{ line_id: string; qty: number }>(
          `SELECT l.sales_order_line_id AS line_id, SUM(l.qty_milli) AS qty
             FROM sales_invoice_lines l JOIN sales_invoices i ON i.id = l.invoice_id
            WHERE i.sales_order_id = ? AND i.status = 'finalized' AND i.deleted_at IS NULL
              AND l.deleted_at IS NULL AND l.sales_order_line_id IS NOT NULL
            GROUP BY l.sales_order_line_id`,
          [id],
        ),
        this.rows(
          'sales_invoices',
          `SELECT * FROM sales_invoices WHERE sales_order_id = ? AND deleted_at IS NULL
            ORDER BY invoice_date DESC, created_at DESC`,
          [id],
        ),
      ])
      return {
        order,
        lines,
        invoicedByLine: Object.fromEntries(invoiced.map((r) => [r.line_id, r.qty])),
        invoices,
      }
    },

    list: async (filter: SalesOrderFilter): Promise<Page<SalesOrderRow>> => {
      const where = ['deleted_at IS NULL', 'kind = ?']
      const params: SqlParam[] = [filter.kind]
      if (filter.status) {
        where.push('status = ?')
        params.push(filter.status)
      }
      if (filter.customerId) {
        where.push('customer_id = ?')
        params.push(filter.customerId)
      }
      if (filter.from) {
        where.push('doc_date >= ?')
        params.push(filter.from)
      }
      if (filter.to) {
        where.push('doc_date <= ?')
        params.push(filter.to)
      }
      if (filter.search?.trim()) {
        const like = `%${escapeLike(filter.search.trim())}%`
        where.push(`(number LIKE ? ESCAPE '\\' OR customer_name LIKE ? ESCAPE '\\')`)
        params.push(like, like)
      }
      const base = `SELECT * FROM sales_orders WHERE ${where.join(' AND ')}`
      const [rows, total] = await Promise.all([
        this.rows(
          'sales_orders',
          `${base} ORDER BY doc_date DESC, created_at DESC LIMIT ? OFFSET ?`,
          [...params, filter.limit ?? DEFAULT_LIMIT, filter.offset ?? 0],
        ),
        this.count(base, params),
      ])
      return { rows, total }
    },

    board: async (filter: SalesOrderBoardFilter): Promise<Page<SalesOrderBoardRow>> => {
      const where = ['kind = ?']
      const params: SqlParam[] = [filter.kind]
      if (filter.show === 'open') where.push(SO_OPEN)
      if (filter.show === 'finished') where.push(`NOT ${SO_OPEN}`)
      if (filter.state === 'draft') where.push(`status = 'draft'`)
      if (filter.state === 'accepted')
        where.push(`${SO_LIVE} AND status = 'accepted' AND _billed = 0`)
      if (filter.state === 'part') where.push(`${SO_LIVE} AND _billed > 0`)
      if (filter.state === 'waiting' || filter.state === 'expired') {
        where.push(
          `${SO_LIVE} AND status IN ('sent', 'confirmed') AND _billed = 0 AND ${filter.state === 'expired' ? '' : 'NOT '}${SO_EXPIRED}`,
        )
        params.push(filter.today)
      }
      if (filter.customerId) {
        where.push('customer_id = ?')
        params.push(filter.customerId)
      }
      if (filter.search?.trim()) {
        const like = `%${escapeLike(filter.search.trim())}%`
        where.push(`(number LIKE ? ESCAPE '\\' OR customer_name LIKE ? ESCAPE '\\')`)
        params.push(like, like)
      }
      const base = `SELECT * FROM (${SO_BOARD}) WHERE ${where.join(' AND ')}`
      // Open: accepted first, then what runs out or is expected soonest, expired, drafts.
      const order =
        filter.show === 'open'
          ? `CASE WHEN status = 'draft' THEN 3 WHEN ${SO_EXPIRED} THEN 2
                  WHEN status = 'accepted' THEN 0 ELSE 1 END,
             COALESCE(valid_until, expected_date) IS NULL,
             COALESCE(valid_until, expected_date, doc_date), created_at DESC`
          : 'doc_date DESC, created_at DESC'
      const [found, total] = await Promise.all([
        this.driver.all<SqlRow>(`${base} ORDER BY ${order} LIMIT ? OFFSET ?`, [
          ...params,
          ...(filter.show === 'open' ? [filter.today] : []),
          filter.limit ?? DEFAULT_LIMIT,
          filter.offset ?? 0,
        ]),
        this.count(base, params),
      ])
      const rows = found.map((r) => {
        const { _lines, _ordered, _billed, _left, _unbilled, _made_order, ...row } = r as Record<
          string,
          SqliteValue
        >
        void _left
        return {
          order: decodeRow('sales_orders', row) as SalesOrderRow,
          lineCount: Number(_lines),
          orderedMilli: Number(_ordered),
          billedMilli: Number(_billed),
          unbilledPaisa: Number(_unbilled),
          madeOrder: Number(_made_order) > 0,
        }
      })
      return { rows, total }
    },

    counts: async (
      kind: SalesOrderRow['kind'],
      today: string,
      soonBy: string,
    ): Promise<SalesOrderCounts> => {
      // The named parameter is today; `?2` is the last day that counts as "soon".
      const expired = SO_EXPIRED.replace('?', '?1')
      const waiting = `${SO_LIVE} AND status IN ('sent', 'confirmed') AND _billed = 0 AND NOT ${expired}`
      const [c] = await this.driver.all<SalesOrderCounts>(
        `SELECT COALESCE(SUM(status = 'draft'), 0) AS draft,
                COALESCE(SUM(${waiting}), 0) AS waiting,
                COALESCE(SUM(${SO_LIVE} AND status = 'accepted' AND _billed = 0), 0) AS accepted,
                COALESCE(SUM(${SO_LIVE} AND _billed > 0), 0) AS part,
                COALESCE(SUM(${SO_LIVE} AND _billed = 0 AND ${expired}), 0) AS expired,
                COALESCE(SUM(NOT ${SO_OPEN}), 0) AS finished,
                COALESCE(SUM(${waiting} AND kind = 'quotation' AND valid_until <= ?2), 0) AS soon,
                COALESCE(SUM(CASE WHEN ${waiting} THEN total_paisa ELSE 0 END), 0) AS waitingPaisa,
                COALESCE(SUM(CASE WHEN ${SO_LIVE} AND (status <> 'sent' OR _billed > 0)
                                  THEN _unbilled ELSE 0 END), 0) AS unbilledPaisa
           FROM (${SO_BOARD}) WHERE kind = ?3`,
        [today, soonBy, kind],
      )
      return c!
    },
  }

  readonly counters = {
    current: async (): Promise<CounterRow | null> => {
      const rows = await this.rows(
        'billing_counters',
        `SELECT * FROM billing_counters WHERE installation_id = ? AND deleted_at IS NULL LIMIT 1`,
        [this.installationId],
      )
      return rows[0] ?? null
    },

    nextSeq: async (docType: NumberedDoc, fiscalYear: string): Promise<number> => {
      const counter = await this.counters.current()
      if (!counter) throw new Error('no_counter: register this device as a billing counter first')
      // Quotations and orders share a table but each has its own series.
      const docTable = {
        sales_invoice: 'sales_invoices',
        credit_note: 'credit_notes',
        purchase_order: 'purchase_orders',
        purchase_request: 'purchase_requests',
        quotation: `sales_orders WHERE kind = 'quotation' AND`,
        sales_order: `sales_orders WHERE kind = 'order' AND`,
      }[docType]
      const from = docTable.endsWith('AND') ? docTable : `${docTable} WHERE`
      const rows = await this.driver.all<{ server: number | null; local: number | null }>(
        `SELECT
           (SELECT MAX(next_seq) FROM counter_sequences WHERE counter_id = ? AND doc_type = ? AND fiscal_year = ?) AS server,
           (SELECT MAX(seq) + 1 FROM ${from} counter_id = ? AND fiscal_year = ?) AS local`,
        [counter.id, docType, fiscalYear, counter.id, fiscalYear],
      )
      return Math.max(rows[0]?.server ?? 1, rows[0]?.local ?? 1)
    },
  }
}
